/* eslint-disable*/
import { useState, useEffect, useCallback, useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Homologate } from './Homologate'
import { PostGraduate } from './PostGraduate'
import { DocumentCard } from './DocumentCard'
import { HomologationSelectors } from './HomologationSelectors'
import {
    useHomologationsApi,
    HomologationByDocumentRequest
} from '@/services/api/homologationsApi'
import { useProviderLoader } from '@/Providers'
import { Tips } from '@/components/common'
import { SubTitle, Text } from '@/components/common/UI'
import { CheckboxControl, FormNavigation } from '@/components/form-controls'
import { useAuthHook } from '@/hooks'
import { usePaymentOrderData } from '@/hooks/usePaymentOrderData'
import { useToastManager } from '@/hooks/useToastManager'
import { useFilesApi } from '@/services/api/filesApi'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { AspirantDocumentsResponse, MessageLevel } from '@/types'
import type { AspirantDocument } from '@/types/documents'
import { ErrorHandlingStrategy, SuccessStatus } from '@/types/api'
import { useFormProgress, saveStudyModality } from '@/utils'
import { getFieldName, getUniqueDocumentId } from '@/utils/documentUtils'

import { Icons } from '@/components/icons'
import { Switch, Tooltip, Link } from '@nextui-org/react'
import { ToastType } from '@/types/forms/uploadFiles'
import { useToast } from '@/context/ToastContext'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@nextui-org/react'
import { DocumentStatus } from '@/types/documents/enums'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { AlertModal } from '@/components/common/AlertModal'
import { HomologationTour, TourStep } from './HomologationTour'

const PAYMENT_CHECK_UNIVERSITY_KEYWORDS = [
    //'politecnico internacional',
    'politec internacional inst. de edu. supe',
    'nueva america',
    //'politecnico santafe',
    'politec santafe de bogota',
    'sena',
]

const matchesPaymentUniversity = (universityText: string): boolean => {
    const normalized = universityText
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
    const matched = PAYMENT_CHECK_UNIVERSITY_KEYWORDS.find((kw) =>
        normalized.includes(kw)
    )
    console.log('[matchesPaymentUniversity]', {
        input: universityText,
        normalized,
        matched: matched ?? null,
        result: Boolean(matched),
    })
    return Boolean(matched)
}

// Solo se puede radicar con la inscripción en estado Matriculado (3),
// Autorizado/Admitido (4), Cerrado (6) o Cerrada con éxito - admitido (10);
// creada, rechazada o Cerrada sin éxito - inscrito (9) no.
// NOTA: Cerrado (6) se dividió en Cerrada sin éxito - inscrito (9) y Cerrada con
// éxito - admitido (10); el 6 se mantiene para inscripciones cerradas antes del cambio.
const INSCRIPTION_STATUS_MATRICULADO_ID = 3
const INSCRIPTION_STATUS_AUTORIZADO_ID = 4
const INSCRIPTION_STATUS_CERRADO_ID = 6
const INSCRIPTION_STATUS_CERRADO_ADMITIDO_ID = 10

const isInscriptionAllowedToFile = (statusId?: number | null): boolean =>
    statusId === INSCRIPTION_STATUS_MATRICULADO_ID ||
    statusId === INSCRIPTION_STATUS_AUTORIZADO_ID ||
    statusId === INSCRIPTION_STATUS_CERRADO_ID ||
    statusId === INSCRIPTION_STATUS_CERRADO_ADMITIDO_ID

interface UploadFilesFormProps {
    onNext: () => void
    onPrevious: () => void
    updateForms?: () => void
    hasNextStep?: boolean
    isReadOnly?: boolean
    onBlockedFieldClick?: () => void
}

export function UploadFilesForm({
    onNext,
    onPrevious,
    updateForms,
    hasNextStep,
    isReadOnly = false,
    onBlockedFieldClick
}: UploadFilesFormProps): JSX.Element {
    const { t } = useTranslation()
    const { control, handleSubmit, watch, reset, setValue } = useForm<any>()
    const { showLoader, hideLoader } = useProviderLoader()
    const { idAspirant, idInscription } = useAuthHook()
    const { idAsesor, updateStudyModality } = useAuthStore()
    const { showToast } = useToast()
    const { operationIds, generateUniqueId, showErrorToast } = useToastManager()
    const filesApi = useFilesApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        timeout: 120000
    })
    const aspirantsApi = useAspirantsApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY
    })
    const inscriptionsApi = useInscriptionsApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY
    })
    const homologationsApi = useHomologationsApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY
    })
    const [loading, setLoading] = useState(false)
    const [documentsData, setDocumentsData] =
        useState<AspirantDocumentsResponse | null>(null)
    const [showAllDocuments, setShowAllDocuments] = useState(false)
    const [uploadInProgress, setUploadInProgress] = useState<
        Record<string, boolean>
    >({})
    const [uploadIndividually, setUploadIndividually] = useState(false)
    const [progressOverride, setProgressOverride] = useState<number | null>(
        null
    )
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
    const [selectedUniversity, setSelectedUniversity] = useState<string>('')
    const [selectedUniversityText, setSelectedUniversityText] =
        useState<string>('')
    const [selectedProgram, setSelectedProgram] = useState<string>('')
    const [universitiesData, setUniversitiesData] = useState<
        Array<{ value: string; label: string }>
    >([])
    const [selectedUniversityData, setSelectedUniversityData] = useState<{
        value: string
        label: string
        country: string
        department: string
        population: string
    } | null>(null)
    // Evitar múltiples intentos de prellenado desde origin/latest
    const originLatestPrefillAttemptedRef = useRef<boolean>(false)
    const scrollPositionRef = useRef<number>(0)
    const [showCompletionAlert, setShowCompletionAlert] = useState(false)
    const [hasShownCompletionAlert, setHasShownCompletionAlert] =
        useState(false)
    // Nuevo estado: tipo de admisión para controlar visibilidad de selectores de homologación
    const [admissionTypeAcademic, setAdmissionTypeAcademic] =
        useState<string>('')

    // Nuevo: control del botón manual de envío de homologación
    const [canSendHomologation, setCanSendHomologation] =
        useState<boolean>(false)
    const [sendingHomologation, setSendingHomologation] =
        useState<boolean>(false)
    const [hasRejectedHomologationDocs, setHasRejectedHomologationDocs] =
        useState(false)
    // Estado: marcar que ya se envió la homologación
    const [hasSentHomologation, setHasSentHomologation] = useState(false)
    // Estado: confirmación para enviar homologación
    const [confirmSendOpen, setConfirmSendOpen] = useState(false)

    const [showDiscountModal, setShowDiscountModal] = useState<boolean | null>(
        null
    )
    const [paymentHomologationOrder, setPaymentHomologationOrder] =
        useState<boolean>(
            import.meta.env.VITE_HOMOLOGATION_ORDER_PAYMENT === 'true'
        )
    const [
        paymentHomologationOrderModalOpen,
        setPaymentHomologationOrderModalOpen
    ] = useState<boolean>(false)

    // Tour state
    const [isTourOpen, setIsTourOpen] = useState(false)

    // Estado de pago de matrícula
    const { paymentDetails, fetchPaymentData } = usePaymentOrderData()
    const isPaymentCompleted =
        paymentDetails?.paymentStatus?.toUpperCase() === 'PAGADO'

    // Tour de homologación
    useEffect(() => {
        // Start tour if homologation (001) and not explicitly disabled
        if (
            admissionTypeAcademic === '001' &&
            !loading &&
            !sendingHomologation &&
            !hasSentHomologation
        ) {
            const isTourDisabled = localStorage.getItem(
                `disableHomologationTour_${idAspirant}`
            )
            // Check if seen in current session
            const hasSeenInSession = sessionStorage.getItem(
                `hasSeenHomologationTourSession_${idAspirant}`
            )

            // Mostrar si no está desactivado permanentemente Y no se ha visto en esta sesión
            if (!isTourDisabled && !hasSeenInSession && documentsData) {
                const timer = setTimeout(() => setIsTourOpen(true), 1500)
                return () => clearTimeout(timer)
            }
        }
    }, [
        admissionTypeAcademic,
        loading,
        sendingHomologation,
        hasSentHomologation,
        idAspirant,
        documentsData
    ])

    const handleTourComplete = (dontShowAgain: boolean) => {
        if (idAspirant) {
            // Always mark as seen for this session
            sessionStorage.setItem(
                `hasSeenHomologationTourSession_${idAspirant}`,
                'true'
            )

            if (dontShowAgain) {
                localStorage.setItem(
                    `disableHomologationTour_${idAspirant}`,
                    'true'
                )
            }
        }
        setIsTourOpen(false)
    }

    const handleTourClose = () => {
        if (idAspirant) {
            // Mark as seen for this session even if closed without completing
            sessionStorage.setItem(
                `hasSeenHomologationTourSession_${idAspirant}`,
                'true'
            )
        }
        setIsTourOpen(false)
    }

    const tourSteps: TourStep[] = [
        {
            targetId: 'tour-university-select',
            title: 'Universidad de Origen',
            content:
                'Busca y selecciona la universidad donde realizaste tus estudios previos.',
            placement: 'bottom'
        },
        {
            targetId: 'tour-program-input',
            title: 'Programa Académico',
            content:
                'Escribe el nombre exacto del programa o carrera que cursaste.',
            placement: 'bottom'
        },
        {
            targetId: 'tour-upload-notes',
            title: 'Certificado de Notas',
            content:
                'Sube aquí tu certificado de calificaciones. Asegúrate de que sea legible.',
            placement: 'top'
        },
        {
            targetId: 'tour-upload-content',
            title: 'Contenido Programático',
            content:
                'Sube aquí el plan de estudios o contenidos. Si eres SENA, este paso es opcional o no aplica.',
            placement: 'top'
        },
        {
            targetId: 'tour-add-university',
            title: 'Agregar Otra Universidad',
            content:
                '¿Estudiaste en más de un lugar? Usa este botón para añadir otra institución.',
            placement: 'top'
        },
        {
            targetId: 'tour-send-button',
            title: 'Enviar Solicitud',
            content:
                '¡Importante! Al finalizar, pulsa este botón para que iniciemos el estudio de tus documentos.',
            placement: 'top'
        }
    ]

    // Cargar tipo de admisión
    useEffect(() => {
        const fetchAdmissionType = async () => {
            if (!idAspirant) return
            try {
                const res = await aspirantsApi.getAcademicAdmission(
                    idAspirant,
                    idInscription
                )
                console.log(
                    '📋 UploadFilesForm - getAcademicAdmission response:',
                    {
                        status: res.status,
                        data: res.data,
                        studyModality: res.data?.studyModality
                    }
                )
                if (SuccessStatus.ANY_SUCCESS(res.status) && res.data) {
                    const at = (res.data as any).admissionType ?? ''
                    const normalized = String(at).padStart(3, '0')
                    setAdmissionTypeAcademic(normalized)

                    // Guardar studyModality usando helper centralizado
                    saveStudyModality(
                        res.data.studyModality,
                        updateStudyModality,
                        'UploadFiles-Init'
                    )
                }
            } catch (e) {
                console.error(
                    '❌ Error al obtener datos académicos en UploadFilesForm:',
                    e
                )
            }
        }
        fetchAdmissionType()
    }, [idAspirant])

    // Consultar estado de pago al cargar el formulario de cargue documental
    useEffect(() => {
        if (!idInscription) return
        fetchPaymentData(idInscription)
    }, [idInscription])
    const handleUniversityChange = useCallback(
        (value: string) => {
            setSelectedUniversity(value)
            if (idAspirant) {
                localStorage.setItem(
                    `uploadFiles_university_${idAspirant}`,
                    value
                )
            }

            // Buscar y guardar la información completa de la universidad
            const universityData = universitiesData.find(
                (uni) => uni.value === value
            )
            if (universityData) {
                const fullData = {
                    value: universityData.value,
                    label: universityData.label,
                    country: (universityData as any).country || '',
                    department: (universityData as any).department || '',
                    population: (universityData as any).population || ''
                }
                setSelectedUniversityData(fullData)

                // También guardar el texto para mostrarlo en el input
                setSelectedUniversityText(universityData.label)
                matchesPaymentUniversity(universityData.label)
                if (idAspirant) {
                    localStorage.setItem(
                        `uploadFiles_universityText_${idAspirant}`,
                        universityData.label
                    )
                }
            } else {
                setSelectedUniversityData(null)
                setSelectedUniversityText('')
                if (idAspirant) {
                    localStorage.removeItem(
                        `uploadFiles_universityText_${idAspirant}`
                    )
                }
            }
        },
        [universitiesData, idAspirant]
    )

    const handleProgramChange = useCallback(
        (value: string) => {
            setSelectedProgram(value)
            if (idAspirant) {
                localStorage.setItem(`uploadFiles_program_${idAspirant}`, value)
            }
        },
        [idAspirant]
    )

    const handleUniversityDataChange = useCallback(
        (
            universities: Array<{
                value: string
                label: string
                country: string
                department: string
                population: string
            }>
        ) => {
            setUniversitiesData(universities)

            // Si hay una universidad seleccionada, actualizar su información completa
            if (selectedUniversity) {
                const universityData = universities.find(
                    (uni) => uni.value === selectedUniversity
                )
                if (universityData) {
                    const fullData = {
                        value: universityData.value,
                        label: universityData.label,
                        country: universityData.country || '',
                        department: universityData.department || '',
                        population: universityData.population || ''
                    }
                    setSelectedUniversityData(fullData)

                    // También actualizar el texto mostrado
                    setSelectedUniversityText(universityData.label)
                }
            }
        },
        [selectedUniversity]
    )

    // Función para consumir el API de homologaciones
    const fetchHomologationData = useCallback(async () => {
        // Fix: Permitir enviar si hay texto de universidad, aunque no haya data estructurada (caso prellenado o manual)
        if (
            !idAspirant ||
            (!selectedUniversityData && !selectedUniversityText) ||
            !selectedProgram
        ) {
            console.warn(
                '❌ [DEBUG] fetchHomologationData abortado por datos faltantes:',
                {
                    idAspirant,
                    selectedUniversityData,
                    selectedProgram,
                    selectedUniversityText
                }
            )
            return
        }

        console.log('🚀 [DEBUG] Iniciando fetchHomologationData con:', {
            idAspirant,
            idInscription,
            selectedUniversityData,
            selectedProgram
        })

        try {
            console.log('🔄 Obteniendo datos básicos del aspirante...')

            // Obtener documentNumber del aspirante
            const basicDataResponse =
                await aspirantsApi.getBasicData(idAspirant)
            console.log('🔄 [DEBUG] Obteniendo basicData...', basicDataResponse)
            if (
                !SuccessStatus.ANY_SUCCESS(basicDataResponse.status) ||
                !basicDataResponse.data
            ) {
                console.error(
                    '❌ [DEBUG] Falló getBasicData:',
                    basicDataResponse.message ?? basicDataResponse.error,
                    basicDataResponse.status
                )
                return
            }
            const documentNumber = basicDataResponse.data.documentNumber
            console.log('✅ [DEBUG] DocumentNumber obtenido:', documentNumber)

            // Obtener academicPlanId del aspirante
            const academicDataResponse =
                await aspirantsApi.getAcademicAdmission(
                    idAspirant,
                    idInscription
                )
            console.log(
                '📋 fetchHomologationData - getAcademicAdmission response:',
                {
                    status: academicDataResponse.status,
                    data: academicDataResponse.data,
                    studyModality: academicDataResponse.data?.studyModality
                }
            )
            if (
                !SuccessStatus.ANY_SUCCESS(academicDataResponse.status) ||
                !academicDataResponse.data
            ) {
                console.error(
                    '❌ [DEBUG] Falló getAcademicAdmission:',
                    academicDataResponse.message ?? academicDataResponse.error,
                    academicDataResponse.status
                )
                return
            }
            const academicPlanId = academicDataResponse.data.program
            console.log('✅ [DEBUG] AcademicPlanId obtenido:', academicPlanId)

            // Guardar studyModality usando helper centralizado
            saveStudyModality(
                academicDataResponse.data.studyModality,
                updateStudyModality,
                'UploadFiles-Homologation'
            )

            // Construir lista de instituciones de origen combinando principal y adicionales
            const originInstitutions = [
                {
                    originInstitutionName:
                        (selectedUniversityText &&
                            selectedUniversityText.trim()) ||
                        selectedUniversityData?.label ||
                        '',
                    programOriginName: selectedProgram
                },
                ...additionalOrigins
                    .filter((s) =>
                        Boolean(s.selectedUniversityText && s.selectedProgram)
                    )
                    .map((s) => ({
                        originInstitutionName: String(s.selectedUniversityText),
                        programOriginName: String(s.selectedProgram)
                    }))
            ]

            const homologationRequest: HomologationByDocumentRequest = {
                idDocumentNumber: documentNumber,
                academicPlanId: academicPlanId,
                originInstitutions
            }

            console.log(
                '📤 [DEBUG] A punto de llamar a getHomologationsByDocument con:',
                JSON.stringify(homologationRequest, null, 2)
            )

            const homologationResponse =
                await homologationsApi.getHomologationsByDocument(
                    homologationRequest
                )

            console.log(
                '📥 [DEBUG] Respuesta cruda de getHomologationsByDocument:',
                homologationResponse
            )

            console.log('📥 Respuesta de homologación:', homologationResponse)

            // Vincular llamado al endpoint de sincronización de documentos
            const isApiResponse =
                homologationResponse &&
                typeof homologationResponse === 'object' &&
                ('status' in homologationResponse ||
                    'data' in homologationResponse)
            const isSuccess = isApiResponse
                ? SuccessStatus.ANY_SUCCESS(
                      (homologationResponse as any).status
                  )
                : true

            if (isSuccess) {
                if (!idInscription) {
                    console.warn(
                        '⚠️ No hay idInscription disponible para enviar correo de revisión'
                    )
                } else {
                    const emailResponse =
                        await aspirantsApi.sendReviewEmailByDocument(
                            homologationRequest.idDocumentNumber,
                            Number(idInscription)
                        )
                    console.log('✉️ Respuesta envío de correo:', emailResponse)
                }

                // idHomologations puede venir como id o idHomologation en la respuesta
                const idHomologations = (
                    isApiResponse
                        ? (homologationResponse as any).data?.id
                        : (homologationResponse as any)?.id
                ) as number | undefined

                if (!idHomologations) {
                    console.warn(
                        '⚠️ No se encontró idHomologations en la respuesta de homologación:',
                        homologationResponse
                    )
                } else if (!idInscription) {
                    console.warn(
                        '⚠️ No hay idInscription disponible para sincronizar documentos'
                    )
                } else if (!idAspirant) {
                    console.warn(
                        '⚠️ No hay idAspirant disponible para sincronizar documentos'
                    )
                } else {
                    console.log(
                        '🔗 Sincronizando documentos del aspirante con homologaciones:',
                        {
                            idAspirant: Number(idAspirant),
                            idInscription: Number(idInscription),
                            idHomologations
                        }
                    )

                    const syncResponse =
                        await homologationsApi.syncAspirantDocuments(
                            Number(idAspirant),
                            Number(idInscription),
                            idHomologations
                        )

                    if (SuccessStatus.ANY_SUCCESS(syncResponse.status)) {
                        console.log(
                            '✅ Sincronización exitosa de documentos de homologación:',
                            syncResponse
                        )
                        // Mostrar alerta de éxito al completar el flujo de homologación
                        showToast({
                            type: 'success',
                            title: 'Información cargada',
                            message:
                                'Se cargó la información de homologación exitosamente.',
                            duration: 5000
                        })
                        // Persistir bandera para evitar toasts duplicados en esta sesión
                        if (idAspirant) {
                            localStorage.setItem(
                                `completionAlertShown_${idAspirant}`,
                                'true'
                            )
                            // Marcar el flujo como ejecutado para evitar reenvíos tras recarga
                            localStorage.setItem(
                                `homologationFlowExecuted_${idAspirant}`,
                                'true'
                            )
                        }
                    } else {
                        console.error(
                            '❌ Falló la sincronización de documentos de homologación:',
                            syncResponse.message ?? syncResponse.error
                        )
                    }
                }
            } else {
                const msg = isApiResponse
                    ? ((homologationResponse as any)?.message ??
                      (homologationResponse as any)?.error)
                    : undefined
                console.error(
                    '❌ La respuesta de homologación no fue exitosa:',
                    msg
                )
            }
        } catch (error) {
            console.error(
                '❌ Error inesperado al consultar homologaciones:',
                error
            )
        }
    }, [
        idAspirant,
        selectedUniversityData,
        selectedProgram,
        aspirantsApi,
        homologationsApi,
        filesApi,
        documentsData,
        selectedUniversityText
    ])

    const handleUniversityTextChange = useCallback(
        (text: string) => {
            setSelectedUniversityText(text)
            if (idAspirant) {
                localStorage.setItem(
                    `uploadFiles_universityText_${idAspirant}`,
                    text
                )
            }
        },
        [idAspirant]
    )
    useEffect(() => {
        const prefillFromHomologations = async () => {
            const loadFromCache = () => {
                if (!idAspirant) return
                console.log('📂 Cargando datos de homologación desde caché...')
                const cachedUni = localStorage.getItem(
                    `uploadFiles_university_${idAspirant}`
                )
                const cachedUniText = localStorage.getItem(
                    `uploadFiles_universityText_${idAspirant}`
                )
                const cachedProg = localStorage.getItem(
                    `uploadFiles_program_${idAspirant}`
                )
                const cachedAdditional = localStorage.getItem(
                    `uploadFiles_additionalOrigins_${idAspirant}`
                )

                if (cachedUni) setSelectedUniversity(cachedUni)
                if (cachedUniText) setSelectedUniversityText(cachedUniText)
                if (cachedProg) setSelectedProgram(cachedProg)
                if (cachedAdditional) {
                    try {
                        const parsed = JSON.parse(cachedAdditional)
                        if (Array.isArray(parsed)) {
                            setAdditionalOrigins(parsed)
                        }
                    } catch (e) {
                        console.error(
                            'Error loading additional origins cache:',
                            e
                        )
                    }
                }
            }

            try {
                // Evitar múltiples intentos (una sola consulta por montaje)
                if (originLatestPrefillAttemptedRef.current) return
                if (
                    !idAspirant ||
                    admissionTypeAcademic !== '001' ||
                    !idInscription
                )
                    return

                const basicData = await aspirantsApi.getBasicData(idAspirant)
                if (
                    !SuccessStatus.ANY_SUCCESS(basicData.status) ||
                    !basicData.data
                ) {
                    return
                }
                const documentNumber = (basicData.data as any)?.documentNumber
                if (!documentNumber) return

                // Marcar intento justo antes de llamar al API (condiciones listas)
                originLatestPrefillAttemptedRef.current = true

                const resp = await homologationsApi.getEnrollmentOriginLatest(
                    String(documentNumber),
                    Number(idInscription),
                    { silent: true }
                )
                // Si no es éxito o no hay data, no hacer nada (sin toasts)
                if (!SuccessStatus.ANY_SUCCESS(resp.status) || !resp.data) {
                    loadFromCache()
                    return
                }

                // El endpoint puede devolver una lista de orígenes; preservar el orden
                const raw = (resp.data as any)?.data ?? (resp.data as any)
                const originsRaw: Array<any> = Array.isArray(raw)
                    ? raw
                    : raw
                      ? [raw]
                      : []
                if (!originsRaw.length) {
                    loadFromCache()
                    return
                }

                // Normalizar y limpiar datos (evitar espacios, claves alternativas)
                const normalizeText = (v: unknown): string =>
                    (v ?? '').toString().trim()

                const parsedOrigins = originsRaw
                    .map((o) => ({
                        uni: normalizeText(
                            (o as any)?.universityName ??
                                (o as any)?.UniversityName
                        ),
                        prog: normalizeText(
                            (o as any)?.programOriginName ??
                                (o as any)?.ProgramOriginName
                        )
                    }))
                    // Filtrar entradas completamente vacías
                    .filter((o) => Boolean(o.uni || o.prog))

                if (!parsedOrigins.length) {
                    loadFromCache()
                    return
                }

                // Si recuperamos datos de homologación, marcamos como enviado para bloquear edición
                console.log(
                    '✅ Datos de homologación encontrados, bloqueando formulario...'
                )
                setHasSentHomologation(true)
                if (idAspirant) {
                    localStorage.setItem(
                        `hasSentHomologation_${idAspirant}`,
                        'true'
                    )
                }

                // Prefill del origen principal con el primer elemento si el usuario no tiene valores
                const first = parsedOrigins[0]
                const firstUni: string | undefined = first.uni || undefined
                const firstProg: string | undefined = first.prog || undefined
                if (firstUni && !selectedUniversityText) {
                    handleUniversityTextChange(firstUni)
                }
                if (firstProg && !selectedProgram) {
                    handleProgramChange(firstProg)
                }

                // Prefill de orígenes adicionales respetando el orden y evitando duplicados
                const restRaw = parsedOrigins.slice(1)
                const seen = new Set<string>()
                const rest = restRaw.filter((r) => {
                    const key = `${r.uni}|${r.prog}`.toLowerCase()
                    if (seen.has(key)) return false
                    seen.add(key)
                    return true
                })
                if (rest.length) {
                    const now = Date.now()
                    // Solo prellenar secciones adicionales que aún no existen o están vacías
                    setAdditionalOrigins((prev) => {
                        // Si ya hay secciones y tienen datos, no sobreescribir; rellenar huecos
                        const updated = [...prev]
                        let idxCounter = 0
                        // Rellenar existentes vacíos primero
                        for (
                            let i = 0;
                            i < updated.length && idxCounter < rest.length;
                            i++
                        ) {
                            const s = updated[i]
                            const uniName: string | undefined =
                                rest[idxCounter].uni || undefined
                            const progName: string | undefined =
                                rest[idxCounter].prog || undefined
                            const isEmpty =
                                !s.selectedUniversityText && !s.selectedProgram
                            if (isEmpty && (uniName || progName)) {
                                updated[i] = {
                                    ...s,
                                    selectedUniversityText: uniName || '',
                                    selectedProgram: progName || ''
                                }
                                idxCounter++
                            }
                        }
                        // Agregar nuevas secciones para los restantes
                        const toAdd = rest
                            .slice(idxCounter)
                            .map((r, addIdx) => ({
                                id: now + addIdx,
                                selectedUniversity: '',
                                selectedUniversityText: r.uni || '',
                                selectedProgram: r.prog || ''
                            }))
                        // Unir y eliminar duplicados ya existentes por seguridad
                        const final = [...updated, ...toAdd]
                        const pairSeen = new Set<string>()
                        for (let i = 0; i < final.length; i++) {
                            const u = (final[i].selectedUniversityText || '')
                                .trim()
                                .toLowerCase()
                            const p = (final[i].selectedProgram || '')
                                .trim()
                                .toLowerCase()
                            const hasPair = Boolean(u || p)
                            if (!hasPair) continue
                            const key = `${u}|${p}`
                            if (pairSeen.has(key)) {
                                // Vaciar duplicados posteriores, respetando datos que el usuario escriba luego
                                final[i] = {
                                    ...final[i],
                                    selectedUniversityText: '',
                                    selectedProgram: ''
                                }
                            } else {
                                pairSeen.add(key)
                            }
                        }
                        return final
                    })
                }
            } catch (e) {
                console.error(
                    '❌ Error al prellenar homologación desde origin/latest:',
                    e
                )
            }
        }

        prefillFromHomologations()
    }, [
        idAspirant,
        idInscription,
        admissionTypeAcademic,
        aspirantsApi,
        homologationsApi,
        selectedUniversityText,
        selectedProgram,
        handleUniversityTextChange,
        handleProgramChange
    ])

    const text = 'uploadFiles'

    // Función para verificar si es un proceso de homologación
    const isHomologationProcess = useCallback(
        (data: AspirantDocumentsResponse) => {
            console.log('🔍 Verificando proceso de homologación...')
            console.log('📊 Data completa recibida:', data)
            console.log('📄 Documentos en data:', data.documents)

            const normalize = (s: string) =>
                s
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')

            const documentNames = data.documents
                .filter((doc) => doc.nameDocumentType) // Filtrar documentos que tengan nombre
                .map((doc) => normalize(doc.nameDocumentType as string))

            console.log('📋 Nombres de documentos encontrados:', documentNames)

            // Verificar si existen los 3 documentos específicos de homologación
            const hasBuenaConducta = documentNames.some((name) => {
                const result =
                    name.includes('conducta') &&
                    name.includes('buena') &&
                    (name.includes('carta') || name.includes('certificado'))
                console.log(
                    `🔍 Verificando "${name}" para documento de buena conducta (carta/certificado): ${result}`
                )
                return result
            })
            const hasCertificadoNotas = documentNames.some((name) => {
                const result =
                    name.includes('certificado') && name.includes('notas')
                console.log(
                    `🔍 Verificando "${name}" para certificado de notas: ${result}`
                )
                return result
            })
            const hasContenidosProgramaticos = documentNames.some((name) => {
                const result =
                    name.includes('contenidos') && name.includes('program')
                console.log(
                    `🔍 Verificando "${name}" para contenidos programaticos: ${result}`
                )
                return result
            })

            console.log(
                '✅ Documento de buena conducta encontrado:',
                hasBuenaConducta
            )
            console.log(
                '✅ Certificado de notas encontrado:',
                hasCertificadoNotas
            )
            console.log(
                '✅ Contenidos programáticos encontrados:',
                hasContenidosProgramaticos
            )

            const isSenaSelected = (selectedUniversityText || '')
                .toUpperCase()
                .includes('-SENA-')

            const isHomologation = isSenaSelected
                ? hasCertificadoNotas
                : hasCertificadoNotas && hasContenidosProgramaticos
            console.log('🎯 Es proceso de homologación:', isHomologation)

            return isHomologation
        },
        [selectedUniversityText]
    )

    // Función para verificar si toda la información está completa
    const checkCompletionStatus = useCallback(
        (data: AspirantDocumentsResponse) => {
            console.log('🚀 Iniciando checkCompletionStatus...')

            // Evitar ejecuciones múltiples con un debounce simple
            if (checkCompletionStatus.isRunning) {
                console.log(
                    '⏸️ checkCompletionStatus ya está ejecutándose, saltando...'
                )
                return
            }
            checkCompletionStatus.isRunning = true

            // Limpiar el flag después de un breve delay
            setTimeout(() => {
                checkCompletionStatus.isRunning = false
            }, 1000)

            // SOLO funcionar si es un proceso de homologación
            if (!isHomologationProcess(data)) {
                console.log('❌ No es un proceso de homologación, saliendo...')
                return
            }

            console.log('✅ Es un proceso de homologación, continuando...')

            // Verificar que universidad y programa estén seleccionados
            const hasUniversityAndProgram =
                selectedUniversity && selectedProgram
            console.log(
                '🏫 Universidad y programa seleccionados:',
                hasUniversityAndProgram
            )
            console.log('🏫 Universidad ID:', selectedUniversity)

            // Mostrar información completa de la universidad
            if (selectedUniversityData) {
                console.log('🏫 Universidad Completa:', {
                    id: selectedUniversityData.value,
                    nombre: selectedUniversityData.label,
                    país: selectedUniversityData.country,
                    departamento: selectedUniversityData.department,
                    población: selectedUniversityData.population
                })
            } else {
                // Fallback: buscar en universitiesData
                const universityName =
                    universitiesData.find(
                        (uni) => uni.value === selectedUniversity
                    )?.label || 'No encontrada'
                console.log('🏫 Universidad Nombre (fallback):', universityName)
            }

            console.log('📚 Programa:', selectedProgram)

            // Verificación previa que usaba TODOS los requeridos
            const requiredDocuments = data.documents.filter(
                (doc) => doc.isRequired
            )
            const validatedDocuments = requiredDocuments.filter(
                (doc) =>
                    doc.idDocumentStatus === DocumentStatus.Aprobado ||
                    doc.idDocumentStatus === DocumentStatus.AprobadoPendiente ||
                    doc.idDocumentStatus === DocumentStatus.Rechazado ||
                    doc.idDocumentStatus ===
                        DocumentStatus.VerificacionHumana ||
                    doc.idDocumentStatus === DocumentStatus.RevisionManual
            )

            console.log(
                '📄 Documentos requeridos (global):',
                requiredDocuments.length
            )
            console.log(
                '✅ Documentos validados (global):',
                validatedDocuments.length
            )
            console.log(
                '📋 Detalle de documentos requeridos (global):',
                requiredDocuments.map((doc) => ({
                    name: doc.nameDocumentType,
                    status: doc.idDocumentStatus,
                    isRequired: doc.isRequired
                }))
            )

            const allRequiredDocumentsValidated =
                requiredDocuments.length > 0 &&
                validatedDocuments.length === requiredDocuments.length

            console.log(
                '🎯 Todos los documentos requeridos validados (global):',
                allRequiredDocumentsValidated
            )

            // NUEVO: Verificar SOLO los documentos propios de homologación
            const isHomologationDoc = (name?: string) => {
                if (!name) return false
                const n = name
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                const isBuenaConducta =
                    n.includes('conducta') &&
                    n.includes('buena') &&
                    (n.includes('carta') || n.includes('certificado'))
                const isCertNotas =
                    n.includes('certificado') && n.includes('notas')
                const isContProg =
                    n.includes('contenidos') && n.includes('program') // programáticos puede venir sin tilde
                return isBuenaConducta || isCertNotas || isContProg
            }

            const homologationDocs = data.documents.filter((doc) =>
                isHomologationDoc(doc.nameDocumentType)
            )
            const validatedHomologationDocs = homologationDocs.filter(
                (doc) =>
                    doc.idDocumentStatus === DocumentStatus.Aprobado ||
                    doc.idDocumentStatus === DocumentStatus.AprobadoPendiente ||
                    doc.idDocumentStatus === DocumentStatus.Rechazado ||
                    doc.idDocumentStatus ===
                        DocumentStatus.VerificacionHumana ||
                    doc.idDocumentStatus === DocumentStatus.RevisionManual
            )

            const allHomologationDocumentsValidated =
                homologationDocs.length >= 3 &&
                validatedHomologationDocs.length >= 3

            console.log(
                '📄 Documentos de homologación encontrados:',
                homologationDocs.length
            )
            console.log(
                '✅ Documentos de homologación validados:',
                validatedHomologationDocs.length
            )
            console.log(
                '🎯 Todos los documentos de homologación validados:',
                allHomologationDocumentsValidated
            )

            // Crear un hash del estado actual de los documentos para detectar cambios
            // Hash de estado para detectar cambios SOLO en los documentos de homologación
            const documentStatesHash = homologationDocs
                .map(
                    (doc) =>
                        `${doc.idAspirantDocument || doc.idDocumentType}_${doc.idDocumentStatus}`
                )
                .sort()
                .join('|')

            const lastShownHash = localStorage.getItem(
                `lastHomologationDocumentStates_${idAspirant}`
            )

            // Bandera persistente para evitar re-ejecuciones innecesarias tras recarga
            const executedKey = `homologationFlowExecuted_${idAspirant}`
            const hasExecutedFlow = localStorage.getItem(executedKey) === 'true'

            console.log('🔑 Hash actual:', documentStatesHash)
            console.log('🔑 Hash anterior:', lastShownHash)
            console.log('🔄 Hash cambió:', lastShownHash !== documentStatesHash)
            console.log(
                '🚨 Ya se mostró alerta antes:',
                hasShownCompletionAlert
            )
            console.log('🚦 Flujo homologación ya ejecutado:', hasExecutedFlow)

            // Si todo está completo y (no se ha mostrado antes O el estado de los documentos cambió)
            if (
                hasUniversityAndProgram &&
                allHomologationDocumentsValidated &&
                (!hasExecutedFlow || lastShownHash !== documentStatesHash)
            ) {
                console.log(
                    '🎉 Completitud alcanzada (homologación): habilitar botón manual de envío'
                )
                // NO mostrar alerta ni consumir APIs automáticamente
                setHasShownCompletionAlert(true)
                // Guardar el hash del estado actual de los documentos
                localStorage.setItem(
                    `lastHomologationDocumentStates_${idAspirant}`,
                    documentStatesHash
                )
            } else {
                console.log(
                    '❌ No se cumplieron las condiciones para mostrar la alerta'
                )
                console.log(
                    '   - Universidad y programa:',
                    hasUniversityAndProgram
                )
                console.log(
                    '   - Documentos de homologación validados:',
                    allHomologationDocumentsValidated
                )
                console.log(
                    '   - No ejecutado antes O hash cambió:',
                    !hasExecutedFlow || lastShownHash !== documentStatesHash
                )
            }
        },
        [
            selectedUniversity,
            selectedProgram,
            hasShownCompletionAlert,
            idAspirant,
            isHomologationProcess,
            fetchHomologationData
        ]
    )

    // Función para obtener estadísticas de documentos
    const getDocumentStats = useCallback(
        (data: AspirantDocumentsResponse | null) => {
            if (!data) return { hasApproved: false, hasRejected: false }

            const requiredDocuments = data.documents.filter(
                (doc) => doc.isRequired
            )
            const hasApproved = requiredDocuments.some(
                (doc) =>
                    doc.idDocumentStatus === DocumentStatus.Aprobado ||
                    doc.idDocumentStatus === DocumentStatus.AprobadoPendiente
            )
            const hasRejected = requiredDocuments.some(
                (doc) => doc.idDocumentStatus === DocumentStatus.Rechazado
            )

            return { hasApproved, hasRejected }
        },
        []
    )

    // Cooldown para evitar doble llamada cuando cambia estado de documento (loadDocuments + progress/smartLoad)
    const lastFormStatusCallRef = useRef<number>(0)
    const FORM_STATUS_COOLDOWN_MS = 800

    // Función para actualizar el form-status (useCallback evita re-ejecuciones del efecto "sistema inteligente")
    const updateFormStatus = useCallback(async () => {
        if (!idAspirant || !idInscription) return

        const now = Date.now()
        if (now - lastFormStatusCallRef.current < FORM_STATUS_COOLDOWN_MS) {
            return
        }
        lastFormStatusCallRef.current = now

        try {
            if (updateForms) {
                updateForms()
            } else {
                await aspirantsApi.getStatusForms(
                    idAspirant,
                    Number(idInscription)
                )
            }
            console.log('Form status updated after documents operation')
        } catch (error) {
            console.error('Error updating form status:', error)
        }
    }, [idAspirant, idInscription, aspirantsApi, updateForms])

    // Función para cargar documentos
    const loadDocuments = async (
        showLoader = true,
        skipFormStatusUpdate = false
    ) => {
        if (!idAspirant) {
            return
        }

        // Evitar múltiples llamadas
        const operationId = generateUniqueId(ToastType.LOAD_FILES)
        if (operationIds.current.has(operationId)) {
            return
        }
        operationIds.current.add(operationId)

        if (showLoader) {
            setLoading(true)
        }
        try {
            const { data, status } = await filesApi.getAspirantDocuments(
                Number(idInscription) || null
            )
            if (status === 200) {
                const newData = data as unknown as AspirantDocumentsResponse
                setDocumentsData(newData)
                setLastUpdate(new Date())
                // Solo actualizar form-status cuando no es la carga inicial (evita llamadas extra al montar)
                if (!skipFormStatusUpdate) {
                    await updateFormStatus()
                }
                // Verificar completitud después de cargar documentos
                checkCompletionStatus(newData)
                const stats = getDocumentStats(newData)
                // No modificar hasSentHomologation aquí; se persiste en localStorage
                // Sincronizar con localStorage por si viene de otra pestaña o flujo previo
                if (idAspirant) {
                    const stored = localStorage.getItem(
                        `hasSentHomologation_${idAspirant}`
                    )
                    const globalStored = localStorage.getItem(
                        'hasSentHomologation_global'
                    )
                    setHasSentHomologation(
                        stored === 'true' || globalStored === 'true'
                    )
                }
            }
        } catch (error) {
            showErrorToast(
                'Error al cargar los documentos',
                ToastType.LOAD_FILES
            )
        } finally {
            if (showLoader) {
                setLoading(false)
            }
            // Eliminar el ID de operación al completar
            operationIds.current.delete(operationId)
        }
    }

    useEffect(() => {
        // Verificar si ya se mostró el alert de completitud para este aspirante
        if (idAspirant) {
            const alertShown = localStorage.getItem(
                `completionAlertShown_${idAspirant}`
            )
            if (alertShown === 'true') {
                setHasShownCompletionAlert(true)
            }
        }

        loadDocuments(true, true)

        // Limpieza al desmontar
        return () => {
            // Eliminar todas las operaciones pendientes al desmontar el componente
            const operationsToRemove = Array.from(operationIds.current).filter(
                (id: string) => id.startsWith(ToastType.LOAD_FILES)
            )

            operationsToRemove.forEach((id: string) => {
                operationIds.current.delete(id)
            })
        }
    }, [idAspirant])

    // Efecto para verificar solicitud de descuento (solo una vez)
    useEffect(() => {
        // Solo ejecutar si aún no se ha evaluado (es null) y hay documentos
        if (showDiscountModal !== null || !documentsData?.documents?.length) {
            return
        }

        // Buscar en documentos adicionales (idAdditionalDocument no es null)
        // Verificar si hay un documento de tipo "solicitudDescuento" sin estado (null/undefined) o con estado "SubeTuDocumento" (1)
        const discountDoc = documentsData.documents.find(
            (doc) =>
                doc.idAdditionalDocument !== null &&
                doc.idAdditionalDocument !== undefined &&
                (doc.nameDocumentType === 'solicitudDescuento' ||
                    doc.nameDocumentType
                        ?.toLowerCase()
                        .includes('descuento')) &&
                (doc.idDocumentStatus === null ||
                    doc.idDocumentStatus === undefined ||
                    doc.idDocumentStatus === DocumentStatus.SubeTuDocumento)
        )

        /* console.log('🔍 Verificando descuento:', {
            hasDocuments: !!documentsData?.documents,
            totalDocs: documentsData?.documents?.length,
            additionalDocs: documentsData.documents.filter(
                (d) =>
                    d.idAdditionalDocument !== null &&
                    d.idAdditionalDocument !== undefined
            ).map(d => ({
                name: d.nameDocumentType,
                idStatus: d.idDocumentStatus,
                idAdditional: d.idAdditionalDocument
            })),
            discountDoc: discountDoc
                ? {
                      name: discountDoc.nameDocumentType,
                      idStatus: discountDoc.idDocumentStatus,
                      idAdditional: discountDoc.idAdditionalDocument
                  }
                : null,
            showDiscountModal
        }) */

        if (discountDoc) {
            setShowDiscountModal(true)
        } else {
            setShowDiscountModal(false)
        }
    }, [documentsData])

    // Sistema inteligente de detección de cambios sin polling constante
    useEffect(() => {
        if (!idAspirant || !idInscription) return

        let lastCheckTime = Date.now()
        let isPageVisible = !document.hidden
        let checkInterval: NodeJS.Timeout | null = null
        let currentDocumentsData = documentsData

        // Verificar si hay documentos pendientes de procesamiento por IA
        const hasPendingDocuments = (
            data: AspirantDocumentsResponse | null
        ) => {
            if (!data || !data.documents) return false
            return data.documents.some(
                (doc) =>
                    doc.status &&
                    doc.status !== 'validation_success' &&
                    doc.status !== 'validation_failed' &&
                    doc.status !== 'rejected' &&
                    doc.status !== 'approved'
            )
        }

        // Función para actualización inteligente
        const smartLoadDocuments = async (force = false) => {
            const now = Date.now()
            const timeSinceLastCheck = now - lastCheckTime

            // Solo actualizar si:
            if (!force && (timeSinceLastCheck < 120000 || !isPageVisible)) {
                return
            }

            try {
                const { data, status } = await filesApi.getAspirantDocuments(
                    Number(idInscription)
                )
                if (status === 200) {
                    const newData = data as unknown as AspirantDocumentsResponse

                    // Comparar con datos anteriores para detectar cambios reales
                    const hasChanges =
                        !currentDocumentsData ||
                        JSON.stringify(currentDocumentsData.documents) !==
                            JSON.stringify(newData.documents)

                    if (hasChanges) {
                        currentDocumentsData = newData
                        setDocumentsData(newData)
                        setLastUpdate(new Date())
                        await updateFormStatus()

                        // Verificar completitud después de actualizar los datos
                        checkCompletionStatus(newData)
                        const stats = getDocumentStats(newData)
                        // Mantener envío de homologación aunque existan rechazos; sincronizar estado persistente
                        if (idAspirant) {
                            const stored = localStorage.getItem(
                                `hasSentHomologation_${idAspirant}`
                            )
                            const globalStored = localStorage.getItem(
                                'hasSentHomologation_global'
                            )
                            setHasSentHomologation(
                                stored === 'true' || globalStored === 'true'
                            )
                        }

                        // Guardar timestamp del último cambio en localStorage
                        localStorage.setItem(
                            `lastDocUpdate_${idAspirant}`,
                            now.toString()
                        )
                        localStorage.setItem(
                            `hasPendingDocs_${idAspirant}`,
                            hasPendingDocuments(newData).toString()
                        )
                    }

                    lastCheckTime = now
                }
            } catch (error) {
                // Errores silenciosos para no molestar al usuario
                console.log('Silent update failed:', error)
            }
        }

        // Verificar cambios cuando la página se vuelve visible
        const handleVisibilityChange = () => {
            const wasVisible = isPageVisible
            isPageVisible = !document.hidden

            if (!wasVisible && isPageVisible) {
                // La página se volvió visible, verificar cambios
                smartLoadDocuments(true)
            }
        }

        // Verificar cambios cuando la ventana recibe focus
        const handleFocus = () => {
            if (isPageVisible) {
                smartLoadDocuments(true)
            }
        }

        // Verificar cambios en localStorage (para detectar cambios de otras pestañas)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === `lastDocUpdate_${idAspirant}` && e.newValue) {
                const lastUpdateTime = parseInt(e.newValue)
                const timeSinceUpdate = Date.now() - lastUpdateTime

                // Si el cambio fue reciente (menos de 5 segundos), actualizar
                if (timeSinceUpdate < 5000) {
                    smartLoadDocuments(true)
                }
            }
        }

        const setupRegularCheck = () => {
            checkInterval = setInterval(() => {
                if (isPageVisible) {
                    smartLoadDocuments()
                }
            }, 15000)
        }

        // Event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', handleFocus)
        window.addEventListener('storage', handleStorageChange)

        // No ejecutar verificación inicial: loadDocuments() ya se ejecutó al montar. Evita duplicar form-status.
        setupRegularCheck()

        // Cleanup
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
            window.removeEventListener('focus', handleFocus)
            window.removeEventListener('storage', handleStorageChange)
            if (checkInterval) {
                clearInterval(checkInterval)
            }
        }
    }, [idAspirant, idInscription, filesApi, updateFormStatus])

    useEffect(() => {
        // Calcular progreso basado en documentos requeridos
        if (documentsData) {
            const requiredDocsCount = requiredDocuments.length
            const requiredDocsUploaded = requiredDocuments.filter(
                (doc) => doc.documentUrl !== null
            ).length

            // Calcular porcentaje y actualizar progreso
            if (requiredDocsCount > 0) {
                const percentage = Math.round(
                    (requiredDocsUploaded / requiredDocsCount) * 100
                )
                setProgressOverride(percentage)
            } else {
                setProgressOverride(0)
            }
        }
    }, [documentsData])

    // Función para subir un documento
    const uploadSingleDocument = async (
        additionalDocumentId: number | null,
        sapDocumentSubtestId: number | null,
        file: File,
        markAsPending: boolean = false,
        originIndex?: number,
        fieldKeyOverride?: string
    ): Promise<boolean> => {
        if (!idAspirant || !idInscription) {
            showErrorToast(
                'No se pudo identificar al aspirante o la inscripción',
                ToastType.SUBMIT
            )
            return false
        }

        // Guardar la posición actual del scroll antes de la subida
        scrollPositionRef.current =
            window.scrollY || document.documentElement.scrollTop || 0

        // Localiza el documento para key/labels usando los IDs reales
        const documentInfo = documentsData?.documents.find(
            (doc) =>
                (additionalDocumentId !== null &&
                    doc.idAdditionalDocument === additionalDocumentId) ||
                (sapDocumentSubtestId !== null &&
                    doc.idSapDocumentSubtest === sapDocumentSubtestId)
        )

        const defaultKey = documentInfo
            ? getFieldName(documentInfo)
            : 'document_upload'
        const fieldKey = fieldKeyOverride || defaultKey
        setUploadInProgress((prev) => ({ ...prev, [fieldKey]: true }))

        try {
            const res = await filesApi.uploadAspirantDocumentToS3(
                idAspirant,
                additionalDocumentId,
                sapDocumentSubtestId,
                Number(idInscription),
                file,
                idAsesor || 0,
                markAsPending,
                originIndex
            )

            if (res && res.status === 200) {
                setValue(fieldKey as any, undefined)
                // Solo mostrar toast si NO es documento pendiente (DocumentCard lo maneja)
                if (documentInfo && !markAsPending) {
                    showToast({
                        message: `${documentInfo.nameDocumentType} ${t('uploadFiles.uploadSuccess')}`,
                        type: 'success'
                    })
                }
                await loadDocuments(false) // No mostrar loader después de subir

                // Restaurar la posición del scroll después de que React termine de renderizar
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: scrollPositionRef.current,
                            behavior: 'auto'
                        })
                    })
                })

                return true
            } else {
                showErrorToast(
                    `${t('uploadFiles.uploadError')}${documentInfo ? `: ${documentInfo.nameDocumentType}` : ''}`,
                    ToastType.SUBMIT
                )
                return false
            }
        } catch (error) {
            console.error('Error uploading document:', error)
            showErrorToast(t('uploadFiles.uploadError'), ToastType.SUBMIT)
            return false
        } finally {
            setUploadInProgress((prev) => ({ ...prev, [fieldKey]: false }))
        }
    }

    const onSubmit: SubmitHandler<any> = async (data) => {
        try {
            if (!idAspirant || !idInscription) {
                showErrorToast(
                    'No se pudo identificar al aspirante o la inscripción',
                    ToastType.SUBMIT
                )
                return
            }

            // Guardar la posición actual del scroll antes de la subida masiva
            scrollPositionRef.current =
                window.scrollY || document.documentElement.scrollTop || 0

            // Recopilar todos los archivos a subir
            const filesToUpload: { id: number; file: File }[] = []

            // Subir todos los documentos que se hayan seleccionado
            for (const [key, value] of Object.entries(data)) {
                // Verificar si el valor es un archivo
                if (
                    value &&
                    typeof value === 'object' &&
                    'name' in value &&
                    'type' in value &&
                    'size' in value
                ) {
                    // Verificar si la clave sigue el patrón "documentType{id}"
                    const documentTypeRegex = /^documentType(\d+)$/
                    const match = key.match(documentTypeRegex)

                    if (match) {
                        const documentTypeId = parseInt(match[1])
                        filesToUpload.push({
                            id: documentTypeId,
                            file: value as File
                        })
                    }
                }
            }

            // Si hay archivos para subir, mostrar un loader global
            if (filesToUpload.length > 0) {
                showLoader()

                try {
                    // Crear array de promesas para subir todos los archivos en paralelo
                    const uploadPromises = filesToUpload.map((fileInfo) => {
                        // Crear un ID único para el toast de este archivo
                        const fileToastId = `file-${fileInfo.id}`
                        const documentInfo = documentsData?.documents.find(
                            (doc) => doc.idDocumentType === fileInfo.id
                        )

                        // Retornar una promesa para este archivo con manejo propio de errores
                        return new Promise<boolean>(async (resolve) => {
                            try {
                                const response =
                                    await filesApi.uploadAspirantDocumentToS3(
                                        idAspirant,
                                        fileInfo.id,
                                        fileInfo.id,
                                        Number(idInscription),
                                        fileInfo.file,
                                        idAsesor || 0,
                                        undefined
                                    )

                                if (response && response.status === 200) {
                                    // Limpiar el campo
                                    setValue(
                                        `documentType${fileInfo.id}` as any,
                                        undefined
                                    )

                                    resolve(true)
                                } else {
                                    resolve(false)
                                }
                            } catch (error) {
                                resolve(false)
                            }
                        })
                    })

                    // Esperar a que todas las promesas se resuelvan, pero NO abortar si una falla
                    const results = await Promise.all(uploadPromises)

                    // Contar cuántos se subieron correctamente
                    const successCount = results.filter(
                        (result) => result === true
                    ).length
                    const failedCount = results.length - successCount

                    if (successCount === filesToUpload.length) {
                        showToast({
                            message: t('uploadFiles.allFilesUploaded', {
                                count: successCount
                            }),
                            type: 'success'
                        })
                        // Si todo salió bien y hay siguiente paso, avanzar
                        if (hasNextStep && onNext) {
                            onNext()
                        }
                    } else if (successCount > 0) {
                        if (failedCount > 0) {
                            showToast({
                                message: t('uploadFiles.someFilesUploaded', {
                                    success: successCount,
                                    failed: failedCount
                                }),
                                type: 'success'
                            })
                        } else {
                            showToast({
                                message: t('uploadFiles.someFilesUploaded', {
                                    success: successCount,
                                    failed: 0
                                }),
                                type: 'success'
                            })
                        }
                        // Avanzar parcialmente? Mejor dejar que el usuario decida o intentar avanzar
                        if (hasNextStep && onNext) {
                            onNext()
                        }
                    } else {
                        showToast({
                            message: t('uploadFiles.uploadError'),
                            type: 'error'
                        })
                    }
                } finally {
                    hideLoader()
                    // Recargar documentos para actualizar la vista sin mostrar loader
                    await loadDocuments(false)

                    // Restaurar la posición del scroll después de que React termine de renderizar
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            window.scrollTo({
                                top: scrollPositionRef.current,
                                behavior: 'auto'
                            })
                        })
                    })
                }
            } else {
                // No hay archivos nuevos
                if (hasNextStep && onNext) {
                    onNext()
                } else {
                    showToast({
                        message: t('uploadFiles.noFilesToUpload'),
                        type: 'success'
                    })
                }
            }
        } catch (error) {
            showErrorToast('Error al enviar el formulario', ToastType.SUBMIT)
            hideLoader()
        }
    }

    // Función para recargar los documentos
    const reloadDocuments = async (showLoader = true) => {
        return loadDocuments(showLoader)
    }

    // Estado y manejadores para confirmación personalizada de eliminación
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const [pendingDeleteId, setPendingDeleteId] = useState<number | undefined>(
        undefined
    )

    const handleRequestDeleteDocument = (idAspirantDocument?: number) => {
        setPendingDeleteId(idAspirantDocument)
        setConfirmDeleteOpen(true)
    }

    const confirmDelete = async () => {
        if (!pendingDeleteId) {
            setConfirmDeleteOpen(false)
            return
        }
        await handleDeleteDocument(pendingDeleteId)
        setConfirmDeleteOpen(false)
        setPendingDeleteId(undefined)
    }

    // Llamar al API para eliminar el documento
    const handleDeleteDocument = async (idAspirantDocument?: number) => {
        // Eliminación sin window.confirm; confirmación se maneja con modal personalizado
        if (!idAspirant || !idAspirantDocument) {
            showErrorToast(
                'No se pudo identificar el documento a eliminar',
                ToastType.DELETE
            )
            return
        }

        // Guardar la posición actual del scroll antes de eliminar
        scrollPositionRef.current =
            window.scrollY || document.documentElement.scrollTop || 0

        showLoader()
        const operationId = generateUniqueId(ToastType.DELETE)
        operationIds.current.add(operationId)

        try {
            const response = await filesApi.softDeleteFile(
                idAspirantDocument.toString()
            )

            if (response && response.status === 200) {
                showToast({
                    message: t('uploadFiles.documentDeleted'),
                    type: 'success'
                })
                await reloadDocuments(false) // No mostrar loader después de eliminar

                // Restaurar la posición del scroll después de que React termine de renderizar
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: scrollPositionRef.current,
                            behavior: 'auto'
                        })
                    })
                })
            } else {
                showErrorToast(t('uploadFiles.deleteError'), ToastType.DELETE)
            }
        } catch (error) {
            showErrorToast(t('uploadFiles.deleteError'), ToastType.DELETE)
        } finally {
            hideLoader()
            operationIds.current.delete(operationId)
        }
    }

    //Nombres especiales de documentos
    const isPlanEPlus = (doc: AspirantDocument) =>
        doc.idAdditionalDocument === 6 ||
        doc.nameDocumentType === 'Plan Carta de plan E+'

    // Documentos requeridos
    const requiredDocuments =
        documentsData?.documents.filter(
            (doc) =>
                // Requeridos normales (SAP)
                (doc.idSapDocumentSubtest !== null &&
                    doc.idSapDocumentSubtest !== undefined) ||
                // Este additional específico también va como requerido
                isPlanEPlus(doc)
        ) || []

    // Otros documentos (adicionales) – excluimos Plan E+ y "Otras comunicaciones" (idAdditionalDocument 8)
    const isOtrasComunicaciones = (doc: AspirantDocument) =>
        doc.idAdditionalDocument === 8

    const otherDocuments =
        documentsData?.documents.filter(
            (doc) =>
                doc.idAdditionalDocument !== null &&
                doc.idAdditionalDocument !== undefined &&
                !isPlanEPlus(doc) &&
                !isOtrasComunicaciones(doc)
        ) || []

    const requiredDocumentsOrdered = [...requiredDocuments].sort((a, b) =>
        getUniqueDocumentId(a).localeCompare(getUniqueDocumentId(b))
    )
    const otherDocumentsOrdered = [...otherDocuments].sort((a, b) =>
        getUniqueDocumentId(a).localeCompare(getUniqueDocumentId(b))
    )
    // Agrupar otros documentos de homologación por "origin index" proveniente del API
    const getOriginKey = (doc: AspirantDocument): string => {
        const anyDoc = doc as any
        const key =
            anyDoc?.originIndex ??
            anyDoc?.origin_index ??
            anyDoc?.idHomologationProcessInstitution ??
            0
        return String(key)
    }
    const groupByOrigin = (docs: AspirantDocument[]): AspirantDocument[][] => {
        const map = new Map<string, AspirantDocument[]>()
        for (const d of docs) {
            const key = getOriginKey(d)
            const arr = map.get(key) || []
            arr.push(d)
            map.set(key, arr)
        }
        // Ordenar por key numérico para mantener el orden de origen
        return Array.from(map.entries())
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([, arr]) => arr)
    }
    const otherDocGroups = groupByOrigin(otherDocumentsOrdered)

    // Check if foreign document message should be shown
    const hasVisaDocument = requiredDocuments.some(
        (doc) => doc.nameDocumentType === 'Fotocopia de visa de estudiante'
    )

    // Detectar si hay documentos de homologación rechazados (Certificado de Notas o Contenido Programático)
    useEffect(() => {
        if (admissionTypeAcademic !== '001') {
            setHasRejectedHomologationDocs(false)
            return
        }

        const isTargetDoc = (name?: string) => {
            if (!name) return false
            const n = name
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
            return (
                (n.includes('certificado') && n.includes('notas')) ||
                (n.includes('contenidos') && n.includes('program'))
            )
        }

        // Revisar todos los documentos "otros" (donde están los de homologación)
        const hasRejected = otherDocumentsOrdered.some(
            (doc) =>
                isTargetDoc(doc.nameDocumentType) &&
                doc.idDocumentStatus === DocumentStatus.Rechazado
        )

        setHasRejectedHomologationDocs(hasRejected)
    }, [admissionTypeAcademic, otherDocumentsOrdered])

    // Control de habilitación del botón "Agregar universidad de origen"
    const [canAddOrigin, setCanAddOrigin] = useState(false)

    // Habilitar "Agregar universidad de origen" solo si el último grupo de documentos
    // de homologación tiene sus tres documentos validados (Aprobado, Rechazado o Verificación Humana)
    useEffect(() => {
        // Requiere tipo de admisión de homologación
        if (admissionTypeAcademic !== '001') {
            setCanAddOrigin(false)
            return
        }

        // Tomar el último grupo (principal o el último adicional creado)
        const latestGroup = otherDocGroups.length
            ? otherDocGroups[otherDocGroups.length - 1]
            : otherDocumentsOrdered

        if (!latestGroup || latestGroup.length === 0) {
            setCanAddOrigin(false)
            return
        }

        const isHomologationDoc = (name?: string) => {
            if (!name) return false
            const n = name
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
            const isCertNotas = n.includes('certificado') && n.includes('notas')
            const isContProg = n.includes('contenidos') && n.includes('program')
            return isCertNotas || isContProg
        }

        const latestIndex = otherDocGroups.length
            ? otherDocGroups.length - 1
            : 0
        const isSenaSelected = (
            latestIndex === 0
                ? selectedUniversityText
                : additionalOrigins[latestIndex - 1]?.selectedUniversityText ||
                  ''
        )
            .toUpperCase()
            .includes('-SENA-')

        const isSenaRequiredDoc = (name?: string) => {
            if (!name) return false
            const n = name
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
            const isCertNotas = n.includes('certificado') && n.includes('notas')
            return isCertNotas
        }

        const docsFilter = isSenaSelected
            ? isSenaRequiredDoc
            : isHomologationDoc
        const requiredCount = isSenaSelected ? 1 : 2
        const homologationDocs = latestGroup.filter((doc) =>
            docsFilter(doc.nameDocumentType)
        )
        const validatedCount = homologationDocs.filter(
            (doc) =>
                doc.idDocumentStatus === DocumentStatus.Aprobado ||
                doc.idDocumentStatus === DocumentStatus.Rechazado ||
                doc.idDocumentStatus === DocumentStatus.VerificacionHumana
        ).length

        const ready =
            homologationDocs.length >= requiredCount &&
            validatedCount >= requiredCount
        setCanAddOrigin(ready)
    }, [
        admissionTypeAcademic,
        otherDocGroups,
        otherDocumentsOrdered,
        documentsData
    ])

    // Calcular progreso real basado en documentos validados (no rechazados)
    const calculateProgress = () => {
        if (requiredDocuments.length === 0) return 0

        // Filtrar documentos que están aprobados o verificados
        const validatedDocs = requiredDocuments.filter(
            (doc) =>
                doc.idDocumentStatus === DocumentStatus.Aprobado ||
                doc.idDocumentStatus === DocumentStatus.AprobadoPendiente ||
                doc.idDocumentStatus === DocumentStatus.VerificacionHumana
        ).length

        return Math.round((validatedDocs / requiredDocuments.length) * 100)
    }

    const progress = calculateProgress()

    // Estado para manejar secciones adicionales de universidad de origen
    const [additionalOrigins, setAdditionalOrigins] = useState<
        Array<{
            id: number
            selectedUniversity?: string
            selectedProgram?: string
            selectedUniversityText?: string
        }>
    >([])

    // Estado de carga para el botón de agregar universidad de origen
    const [addingOrigin, setAddingOrigin] = useState<boolean>(false)
    // Estado de eliminación por sección adicional
    const [deletingOrigin, setDeletingOrigin] = useState<
        Record<number, boolean>
    >({})

    const addOriginSection = (): void => {
        setAdditionalOrigins((prev) => [
            ...prev,
            {
                id: Date.now(),
                selectedUniversity: '',
                selectedProgram: '',
                selectedUniversityText: ''
            }
        ])
    }

    // Handler: crear documentos adicionales en backend y evitar múltiples clics
    const handleAddOriginSection = async (): Promise<void> => {
        if (addingOrigin) return
        if (!idInscription) {
            showToast({
                message:
                    'No hay una inscripción activa para crear documentos adicionales.',
                type: 'error'
            })
            return
        }

        setAddingOrigin(true)
        try {
            const resp = await filesApi.createHomologationAdditionalDocuments(
                Number(idInscription)
            )

            if (SuccessStatus.ANY_SUCCESS(resp.status)) {
                showToast({
                    message: 'Universidad de origen adicional creada.',
                    type: 'success'
                })
                // Recargar documentos para reflejar los nuevos
                await loadDocuments(false)
                // Mostrar todas las secciones para que el usuario vea el nuevo bloque
                setShowAllDocuments(true)
            } else {
                showToast({
                    message:
                        'No se pudo crear los documentos adicionales de homologación.',
                    type: 'error'
                })
            }
        } catch (error) {
            showToast({
                message:
                    'Error al crear los documentos adicionales de homologación.',
                type: 'error'
            })
        } finally {
            setAddingOrigin(false)
        }
    }

    // Handler de eliminación: inactivar documentos del origen en backend
    const handleRemoveOriginSection = async (
        sectionId: number,
        sectionIndex: number
    ): Promise<void> => {
        if (deletingOrigin[sectionId]) return
        if (!idInscription) {
            showToast({
                message:
                    'No hay una inscripción activa para inactivar documentos.',
                type: 'error'
            })
            return
        }

        // Obtener originIndex del grupo de documentos asociado a esta sección
        const groupDocs = otherDocGroups[sectionIndex + 1] || []
        const originIndex = groupDocs.length
            ? Number(getOriginKey(groupDocs[0]))
            : sectionIndex + 1

        setDeletingOrigin((prev) => ({ ...prev, [sectionId]: true }))
        try {
            const resp =
                await filesApi.inactivateHomologationAdditionalDocuments(
                    Number(idInscription),
                    originIndex
                )
            if (SuccessStatus.ANY_SUCCESS(resp.status)) {
                showToast({
                    message:
                        'Documentos de la universidad adicional inactivados.',
                    type: 'success'
                })
                // Remover la sección localmente
                removeOriginSection(sectionId)
                // Recargar documentos para reflejar cambios
                await loadDocuments(false)
            } else {
                showToast({
                    message:
                        'No se pudo inactivar los documentos de esta universidad.',
                    type: 'error'
                })
            }
        } catch (error) {
            showToast({
                message:
                    'Error al inactivar documentos de la universidad adicional.',
                type: 'error'
            })
        } finally {
            setDeletingOrigin((prev) => {
                const { [sectionId]: _, ...rest } = prev
                return rest
            })
        }
    }

    const removeOriginSection = (id: number): void => {
        setAdditionalOrigins((prev) => {
            const newState = prev.filter((s) => s.id !== id)
            if (idAspirant) {
                localStorage.setItem(
                    `uploadFiles_additionalOrigins_${idAspirant}`,
                    JSON.stringify(newState)
                )
            }
            return newState
        })
    }

    const updateOriginSection = (
        id: number,
        updates: Partial<{
            selectedUniversity: string
            selectedProgram: string
            selectedUniversityText: string
        }>
    ): void => {
        setAdditionalOrigins((prev) => {
            const newState = prev.map((s) =>
                s.id === id ? { ...s, ...updates } : s
            )
            if (idAspirant) {
                localStorage.setItem(
                    `uploadFiles_additionalOrigins_${idAspirant}`,
                    JSON.stringify(newState)
                )
            }
            return newState
        })
    }

    // Crear secciones adicionales automáticamente si hay más grupos de documentos por origen
    useEffect(() => {
        const needed = otherDocGroups.length > 1 ? otherDocGroups.length - 1 : 0
        if (needed > 0) {
            setAdditionalOrigins((prev) => {
                if (prev.length >= needed) return prev
                const toAdd = needed - prev.length
                const now = Date.now()
                const newSections = Array.from({ length: toAdd }).map(
                    (_, idx) => ({
                        id: now + idx
                    })
                )
                return [...prev, ...newSections]
            })
        }
        // No removemos secciones si disminuye; el usuario podría haberlas creado manualmente
    }, [documentsData])

    // Calcular habilitación del botón manual de envío
    useEffect(() => {
        if (!documentsData) {
            setCanSendHomologation(false)
            return
        }
        // Debe ser proceso de homologación y tipo de admisión correcto
        const isHomologation = isHomologationProcess(documentsData)
        const mainComplete = Boolean(
            (selectedUniversity || selectedUniversityText) && selectedProgram
        )

        // Validación de documentos de homologación
        const isHomologationDoc = (name?: string) => {
            if (!name) return false
            const n = name
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
            const isCertNotas = n.includes('certificado') && n.includes('notas')
            const isContProg = n.includes('contenidos') && n.includes('program')
            return isCertNotas || isContProg
        }
        const isSenaSelected = (selectedUniversityText || '')
            .toUpperCase()
            .includes('-SENA-')
        const isSenaRequiredDoc = (name?: string) => {
            if (!name) return false
            const n = name
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
            const isCertNotas = n.includes('certificado') && n.includes('notas')
            return isCertNotas
        }
        const homologationDocs = documentsData.documents.filter((doc) =>
            (isSenaSelected ? isSenaRequiredDoc : isHomologationDoc)(
                doc.nameDocumentType
            )
        )
        const validatedHomologationDocs = homologationDocs.filter(
            (doc) =>
                doc.idDocumentStatus === DocumentStatus.Aprobado ||
                doc.idDocumentStatus === DocumentStatus.Rechazado ||
                doc.idDocumentStatus === DocumentStatus.VerificacionHumana
        )
        const requiredCount = isSenaSelected ? 1 : 2
        const docsReadyMain =
            homologationDocs.length >= requiredCount &&
            validatedHomologationDocs.length >= requiredCount

        const groups = otherDocGroups.length
            ? otherDocGroups
            : [otherDocumentsOrdered]

        const groupDocsReady = (group: any[], isSena: boolean) => {
            const filterFn = isSena ? isSenaRequiredDoc : isHomologationDoc
            const reqCount = isSena ? 1 : 2
            const docs = group.filter((d) => filterFn(d.nameDocumentType))
            const validated = docs.filter(
                (d) =>
                    d.idDocumentStatus === DocumentStatus.Aprobado ||
                    d.idDocumentStatus === DocumentStatus.Rechazado ||
                    d.idDocumentStatus === DocumentStatus.VerificacionHumana
            )
            return docs.length >= reqCount && validated.length >= reqCount
        }

        const allGroupsReady = groups.every((group, idx) => {
            const isSenaGroup = (
                idx === 0
                    ? selectedUniversityText
                    : additionalOrigins[idx - 1]?.selectedUniversityText || ''
            )
                .toUpperCase()
                .includes('-SENA-')
            return groupDocsReady(group, isSenaGroup)
        })

        const additionalComplete =
            additionalOrigins.length === 0 ||
            additionalOrigins.every((s) =>
                Boolean(
                    s.selectedUniversity &&
                        s.selectedUniversityText &&
                        s.selectedProgram
                )
            )

        const enabled =
            admissionTypeAcademic === '001' &&
            isHomologation &&
            mainComplete &&
            additionalComplete &&
            docsReadyMain &&
            allGroupsReady
        setCanSendHomologation(enabled)
    }, [
        documentsData,
        selectedUniversity,
        selectedUniversityText,
        selectedProgram,
        additionalOrigins,
        admissionTypeAcademic,
        isHomologationProcess
    ])

    // Inicializar estado enviado desde localStorage en recarga
    useEffect(() => {
        if (!idAspirant) return
        const key = `hasSentHomologation_${idAspirant}`
        const stored = localStorage.getItem(key)
        const globalStored = localStorage.getItem('hasSentHomologation_global')
        if (stored === 'true' || globalStored === 'true') {
            setHasSentHomologation(true)
        } else {
            setHasSentHomologation(false)
        }
    }, [idAspirant])

    // Envío de homologación (tras confirmar)
    const startHomologationSend = async (): Promise<void> => {
        if (sendingHomologation) return
        setConfirmSendOpen(false)
        setSendingHomologation(true)
        try {
            await fetchHomologationData()
            setHasSentHomologation(true)
            if (idAspirant) {
                localStorage.setItem(
                    `hasSentHomologation_${idAspirant}`,
                    'true'
                )
            } else {
                localStorage.setItem('hasSentHomologation_global', 'true')
            }
        } finally {
            setSendingHomologation(false)
        }
    }

    // Handler de envío manual con confirmación
    const handleManualHomologation = async () => {
        if (!canSendHomologation) {
            showToast({
                type: 'warning',
                title: 'Completa la información',
                message:
                    'Debes completar universidad y programa de origen y validar los documentos de homologación. Si agregaste universidades de origen adicionales, completa sus datos.',
                duration: 5000
            })
            return
        }
        if (sendingHomologation || hasSentHomologation) return

        let inscriptionInfo: Awaited<
            ReturnType<typeof inscriptionsApi.getInscription>
        >
        try {
            inscriptionInfo = await inscriptionsApi.getInscription(
                String(idInscription)
            )
        } catch (error) {
            console.error('❌ Error al consultar la inscripción:', error)
            return showToast({
                type: 'error',
                title: 'Error al validar la inscripción',
                message:
                    'No se pudo validar el estado de tu inscripción. Intenta de nuevo.'
            })
        }

        //*Solo se radica con la inscripción Autorizada, Matriculada o Cerrada con éxito
        if (
            !SuccessStatus.ANY_SUCCESS(inscriptionInfo.status) ||
            !isInscriptionAllowedToFile(inscriptionInfo.data?.inscriptionStatusId)
        ) {
            return showToast({
                type: 'warning',
                title: 'Inscripción no autorizada',
                message:
                    'Tu inscripción debe estar Autorizada, Matriculada o Cerrada con éxito para radicar la homologación.',
                duration: 5000
            })
        }

        //*Excluir inscripciones con aliado del pago de homologacion
        if (inscriptionInfo.data.aliadosId) return setConfirmSendOpen(true)

        //*Excluir universidades del pago de homologacion
        const justOneUniversity = additionalOrigins.length === 0
        const matchesPaymentUniversityResult = matchesPaymentUniversity(selectedUniversityText)
        if (matchesPaymentUniversityResult && justOneUniversity) return setConfirmSendOpen(true)

        const allAdditionalOriginsMatch =
            matchesPaymentUniversityResult &&
            additionalOrigins.every((origin) =>
                matchesPaymentUniversity(origin.selectedUniversityText || '')
            )
        if (allAdditionalOriginsMatch) return setConfirmSendOpen(true)

        if (paymentHomologationOrder) {
            try {
                showLoader()
                const resp = await aspirantsApi.getHomologationOrderPayment(
                    Number(idAspirant),
                    Number(idInscription)
                )
                if (SuccessStatus.ANY_SUCCESS(resp.status)) {
                    if (resp.data.length === 0) {
                    return  setPaymentHomologationOrderModalOpen(true)
                    }
                } else {
                    return showToast({
                        type: 'error',
                        title: 'Error al obtener el pago de la homologación',
                        message: 'Error al obtener el pago de la homologación'
                    })
                }
            } catch (error) {
                console.error(
                    '❌ Error al obtener el pago de la homologación:',
                    error
                )
                return showToast({
                    type: 'error',
                    title: 'Error al obtener el pago de la homologación',
                    message: 'Error al obtener el pago de la homologación'
                })
            } finally {
                hideLoader()
            }
            setConfirmSendOpen(true)
        }else{
            setConfirmSendOpen(true)
        }
    }

    // Debug: Log del progreso y documentos
    useEffect(() => {
        console.log('📊 Document Progress Debug:', {
            progress,
            totalRequired: requiredDocuments.length,
            documentsStatus: requiredDocuments.map((doc) => ({
                name: doc.documentName,
                status: doc.idDocumentStatus,
                statusName: doc.statusName
            }))
        })
    }, [progress, requiredDocuments])

    // Estado para controlar si ya se actualizó el form-status cuando llegó al 100%
    const [hasUpdatedFormStatus, setHasUpdatedFormStatus] = useState(false)
    const previousProgress = useRef<number | null>(null)
    const isInitialMount = useRef(true)

    // Refs estables para evitar re-ejecuciones del efecto por cambios de referencia
    const idAspirantRef = useRef(idAspirant)
    useEffect(() => {
        idAspirantRef.current = idAspirant
    }, [idAspirant])

    // Verificar cambios en el progreso y actualizar form-status automáticamente
    useEffect(() => {
        // Evitar ejecución en el primer render (inicialización) para no disparar form-status al montar
        if (isInitialMount.current) {
            isInitialMount.current = false
            previousProgress.current = progress
            return
        }

        // Solo procesar si hay cambio real en el progreso y tenemos idAspirant
        if (
            previousProgress.current !== null &&
            progress !== previousProgress.current &&
            idAspirantRef.current
        ) {
            const wasAt100 = previousProgress.current === 100
            const isAt100 = progress === 100

            console.log('🔄 Progress changed:', {
                from: previousProgress.current,
                to: progress,
                wasAt100,
                isAt100
            })

            // Usar updateFormStatus centralizado (incluye cooldown para evitar doble llamada)
            const triggerUpdate = (reason: string) => {
                console.log(`🔄 ${reason}, updating form status...`)
                updateFormStatus()
            }

            // Caso 1: Progreso llega al 100% (de cualquier valor menor)
            if (isAt100 && !wasAt100) {
                triggerUpdate('progress reached 100%')
                setHasUpdatedFormStatus(true)
            }
            // Caso 2: Progreso baja del 100% (a cualquier valor menor)
            else if (!isAt100 && wasAt100) {
                triggerUpdate('progress dropped below 100%')
                setHasUpdatedFormStatus(false)
            }
            // Caso 3: Cambio dentro del rango < 100% (no necesita actualizar form-status)
            else if (!isAt100 && !wasAt100) {
                console.log(
                    '📊 Progress changed within <100% range, no form-status update needed'
                )
            }
        }

        previousProgress.current = progress
    }, [progress, updateFormStatus])

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full space-y-4"
            >
                <section>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                        <Tips
                            text={t('tips.uploadFiles')}
                            className="mt-8 sm:mt-0"
                        />
                        <div className="flex items-center gap-3 justify-center sm:justify-end">
                            {/* Indicador de actualización automática */}
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse flex-shrink-0"></div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-xs leading-tight">
                                        Estamos revisando tus documentos. Puedes
                                        seguir navegando.
                                    </span>
                                    {lastUpdate && (
                                        <span className="text-xs text-gray-500 leading-tight">
                                            Última actualización:{' '}
                                            {lastUpdate.toLocaleTimeString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {/* Botón de recarga manual (opcional) */}
                            <Tooltip content="Actualizar manualmente">
                                <Button
                                    isIconOnly
                                    color="primary"
                                    variant="light"
                                    size="sm"
                                    onPress={() => loadDocuments(true)}
                                    isDisabled={loading}
                                    className="flex-shrink-0"
                                >
                                    <Icons.Spinner
                                        className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                                    />
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center p-12 bg-white rounded-lg shadow-sm border">
                            <Icons.Spinner className="w-8 h-8 animate-spin text-primary" />
                            <Text
                                text="Cargando documentos..."
                                className="ml-3"
                            />
                        </div>
                    ) : (
                        <>
                            {/* Documentos requeridos */}
                            {requiredDocuments.length > 0 && (
                                <div className="mb-8">
                                    <div className="flex items-center mb-4 pb-2 border-b">
                                        <Icons.ExclamationTriangle className="w-5 h-5 text-primary mr-2" />
                                        <SubTitle subTitle="Documentos Requeridos" />
                                    </div>

                                    {hasVisaDocument && (
                                        <div className="mb-8 p-4 bg-white border border-green-100 rounded-2xl shadow-[0_4px_20px_-10px_rgba(34,197,94,0.1)] flex flex-col sm:flex-row gap-4 items-center sm:items-start transition-all hover:shadow-[0_8px_30px_-12px_rgba(34,197,94,0.15)]">
                                            <div className="flex-shrink-0 w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600 border border-green-50">
                                                <Icons.IosAlert className="w-6 h-6" />
                                            </div>
                                            <div className="flex-1 text-center sm:text-left">
                                                <div className="mb-2">
                                                    <span className="bg-green-100 text-green-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md mb-1.5 inline-block">
                                                        Importante
                                                    </span>
                                                    <p className="text-gray-800 font-semibold text-sm leading-snug">
                                                        {t(
                                                            'uploadFiles.foreignDocumentsInstructionPart1'
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="mb-3 flex justify-center sm:justify-start">
                                                    <Link
                                                        href="https://universidadean.edu.co/sites/default/files/admisiones/guia-proceso-admision-estudiantes-extranjeros.pdf"
                                                        isExternal
                                                        className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-xs shadow-md shadow-green-100 transition-all flex items-center gap-2 w-fit no-underline group/link"
                                                    >
                                                        {t(
                                                            'uploadFiles.foreignDocumentsLinkText'
                                                        )}
                                                        <Icons.AngleRight className="w-3 h-3 text-white group-hover/link:translate-x-0.5 transition-transform" />
                                                    </Link>
                                                </div>

                                                <p className="text-gray-500 text-xs italic border-t border-gray-50 pt-2 flex items-center justify-center sm:justify-start gap-2">
                                                    <Icons.CheckCircle className="w-3 h-3 text-green-400" />
                                                    {t(
                                                        'uploadFiles.foreignDocumentsInstructionPart2'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {requiredDocumentsOrdered.map(
                                            (document, index) => {
                                                console.log(
                                                    `📋 REQUIRED DOCUMENT ${index}:`,
                                                    {
                                                        idDocumentType:
                                                            document.idDocumentType,
                                                        nameDocumentType:
                                                            document.nameDocumentType,
                                                        idSapDocumentSubtest:
                                                            document.idSapDocumentSubtest,
                                                        idAdditionalDocument:
                                                            document.idAdditionalDocument,
                                                        fieldName:
                                                            getFieldName(
                                                                document
                                                            ),
                                                        key: getFieldName(
                                                            document
                                                        )
                                                    }
                                                )

                                                return (
                                                    <DocumentCard
                                                        key={getFieldName(
                                                            document,
                                                            index
                                                        )}
                                                        document={document}
                                                        control={control}
                                                        watch={watch}
                                                        uploadIndividually={
                                                            false
                                                        }
                                                        uploadInProgress={
                                                            uploadInProgress
                                                        }
                                                        onDeleteDocument={
                                                            handleRequestDeleteDocument
                                                        }
                                                        onUploadSingleDocument={(
                                                            additionalDocumentId,
                                                            sapDocumentSubtestId,
                                                            file
                                                        ) =>
                                                            uploadSingleDocument(
                                                                additionalDocumentId,
                                                                sapDocumentSubtestId,
                                                                file,
                                                                false, // markAsPending
                                                                Number(
                                                                    getOriginKey(
                                                                        document
                                                                    )
                                                                )
                                                            )
                                                        }
                                                        onUploadWithHelp={(
                                                            additionalDocumentId,
                                                            sapDocumentSubtestId,
                                                            file
                                                        ) =>
                                                            uploadSingleDocument(
                                                                additionalDocumentId,
                                                                sapDocumentSubtestId,
                                                                file,
                                                                true, // markAsPending
                                                                Number(
                                                                    getOriginKey(
                                                                        document
                                                                    )
                                                                ) // originIndex
                                                            )
                                                        }
                                                        text={text}
                                                        isPaymentCompleted={
                                                            isPaymentCompleted
                                                        }
                                                    />
                                                )
                                            }
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Botón para mostrar/ocultar otros documentos */}
                            {/* {otherDocuments.length > 0 && (
                                <div className="mb-6 mt-8">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowAllDocuments(
                                                !showAllDocuments
                                            )
                                        }
                                        className="flex items-center gap-2 text-blue-600 font-medium py-3 px-4 rounded-md border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors w-full justify-center shadow-sm"
                                    >
                                        {(() => {
                                            const isContProgram = (
                                                name?: string
                                            ) => {
                                                if (!name) return false
                                                const n = name
                                                    .toLowerCase()
                                                    .normalize('NFD')
                                                    .replace(
                                                        /[\u0300-\u036f]/g,
                                                        ''
                                                    )
                                                return (
                                                    n.includes('contenidos') &&
                                                    n.includes('program')
                                                )
                                            }

                                            const docsMain =
                                                otherDocGroups[0] ||
                                                otherDocumentsOrdered
                                            const isSenaMain = (
                                                selectedUniversityText || ''
                                            )
                                                .toUpperCase()
                                                .includes('-SENA-')
                                            const normalize = (name?: string) =>
                                                (name || '')
                                                    .toLowerCase()
                                                    .normalize('NFD')
                                                    .replace(
                                                        /[\u0300-\u036f]/g,
                                                        ''
                                                    )
                                            const isCertNotas = (
                                                name?: string
                                            ) => {
                                                const n = normalize(name)
                                                return (
                                                    n.includes('certificado') &&
                                                    n.includes('notas')
                                                )
                                            }

                                            // Función para identificar si es documento de homologación
                                            const isHomologationDoc = (
                                                name?: string
                                            ) => {
                                                if (!name) return false
                                                return (
                                                    isCertNotas(name) ||
                                                    isContProgram(name)
                                                )
                                            }

                                            // Contar documentos de homologación Y documentos de descuento
                                            const mainCount = isSenaMain
                                                ? docsMain.filter(
                                                      (d) =>
                                                          isCertNotas(
                                                              d.nameDocumentType
                                                          ) ||
                                                          !isHomologationDoc(
                                                              d.nameDocumentType
                                                          ) // Incluir documentos de descuento
                                                  ).length
                                                : docsMain.filter(
                                                      (d) =>
                                                          isCertNotas(
                                                              d.nameDocumentType
                                                          ) ||
                                                          isContProgram(
                                                              d.nameDocumentType
                                                          ) ||
                                                          !isHomologationDoc(
                                                              d.nameDocumentType
                                                          ) // Incluir documentos de descuento
                                                  ).length
                                            let total = mainCount

                                            for (
                                                let gi = 1;
                                                gi < otherDocGroups.length;
                                                gi++
                                            ) {
                                                const groupDocs =
                                                    otherDocGroups[gi] || []
                                                const section =
                                                    additionalOrigins[gi - 1]
                                                const isSenaSection = (
                                                    section?.selectedUniversityText ||
                                                    ''
                                                )
                                                    .toUpperCase()
                                                    .includes('-SENA-')
                                                // Para grupos adicionales, también incluir documentos de descuento
                                                const groupCount = isSenaSection
                                                    ? groupDocs.filter(
                                                          (d) =>
                                                              isCertNotas(
                                                                  d.nameDocumentType
                                                              ) ||
                                                              !isHomologationDoc(
                                                                  d.nameDocumentType
                                                              ) // Incluir documentos de descuento
                                                      ).length
                                                    : groupDocs.filter(
                                                          (d) =>
                                                              isCertNotas(
                                                                  d.nameDocumentType
                                                              ) ||
                                                              isContProgram(
                                                                  d.nameDocumentType
                                                              ) ||
                                                              !isHomologationDoc(
                                                                  d.nameDocumentType
                                                              ) // Incluir documentos de descuento
                                                      ).length
                                                total += groupCount
                                            }

                                            return showAllDocuments ? (
                                                <>
                                                    <Icons.ArrowRight className="w-5 h-5 transform -rotate-90" />
                                                    Ocultar otros documentos (
                                                    {total})
                                                </>
                                            ) : (
                                                <>
                                                    <Icons.AngleDown className="w-5 h-5" />
                                                    Mostrar otros documentos (
                                                    {total})
                                                </>
                                            )
                                        })()}
                                    </button>
                                </div>
                            )} */}

                            {/* Otros documentos */}
                            {otherDocuments.length > 0 && (
                                <div className="mb-8 mt-3 pb-4 border-b">
                                    {/* Documentos de descuento */}
                                    {(() => {
                                        const isDiscountDoc = (
                                            name?: string
                                        ) => {
                                            console.log(
                                                'name discount doc',
                                                name
                                            )
                                            if (!name) return false
                                            return name ===
                                                'Solicitud de descuento'
                                                ? true
                                                : false
                                        }

                                        const docs =
                                            otherDocGroups[0] ||
                                            otherDocumentsOrdered

                                        return docs.filter((d) =>
                                            isDiscountDoc(d.nameDocumentType)
                                        )
                                    })().map((document, index) => {
                                        console.log(
                                            `📄 DOCUMENT Discount ${index}:`,
                                            {
                                                idDocumentType:
                                                    document.idDocumentType,
                                                nameDocumentType:
                                                    document.nameDocumentType,
                                                idSapDocumentSubtest:
                                                    document.idSapDocumentSubtest,
                                                idAdditionalDocument:
                                                    document.idAdditionalDocument,
                                                fieldName:
                                                    getFieldName(document),
                                                key: getFieldName(document)
                                            }
                                        )

                                        return (
                                            <div>
                                                <div className="flex items-center mb-4 pb-2 border-b">
                                                    <Icons.Percent className="w-5 h-5 text-gray-500 mr-2" />
                                                    <SubTitle subTitle="Documento de Descuento" />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <DocumentCard
                                                        key={getFieldName(
                                                            document,
                                                            index
                                                        )}
                                                        document={document}
                                                        control={control}
                                                        watch={watch}
                                                        uploadIndividually={
                                                            false
                                                        }
                                                        uploadInProgress={
                                                            uploadInProgress
                                                        }
                                                        onDeleteDocument={
                                                            handleRequestDeleteDocument
                                                        }
                                                        onUploadSingleDocument={(
                                                            additionalDocumentId,
                                                            sapDocumentSubtestId,
                                                            file
                                                        ) =>
                                                            uploadSingleDocument(
                                                                additionalDocumentId,
                                                                sapDocumentSubtestId,
                                                                file,
                                                                false, // markAsPending
                                                                Number(
                                                                    getOriginKey(
                                                                        document
                                                                    )
                                                                )
                                                            )
                                                        }
                                                        onUploadWithHelp={(
                                                            additionalDocumentId,
                                                            sapDocumentSubtestId,
                                                            file
                                                        ) =>
                                                            uploadSingleDocument(
                                                                additionalDocumentId,
                                                                sapDocumentSubtestId,
                                                                file,
                                                                true, // markAsPending
                                                                Number(
                                                                    getOriginKey(
                                                                        document
                                                                    )
                                                                ) // originIndex
                                                            )
                                                        }
                                                        text={text}
                                                        isPaymentCompleted={
                                                            isPaymentCompleted
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Documentos de homologación */}
                                    {/* Carga de admissionType gestionada en un useEffect fuera del JSX */}
                                    {admissionTypeAcademic === '001' && (
                                        <>
                                            <div className="flex items-center mb-4 pb-2 border-b mt-8">
                                                <Icons.BookIcon className="w-5 h-5 text-gray-500 mr-2" />
                                                <SubTitle subTitle="Documentos de Homologación" />
                                            </div>
                                            <HomologationSelectors
                                                selectedUniversity={
                                                    selectedUniversity
                                                }
                                                selectedProgram={
                                                    selectedProgram
                                                }
                                                selectedUniversityText={
                                                    selectedUniversityText
                                                }
                                                onUniversityChange={
                                                    handleUniversityChange
                                                }
                                                onProgramChange={
                                                    handleProgramChange
                                                }
                                                onUniversityDataChange={
                                                    handleUniversityDataChange
                                                }
                                                onUniversityTextChange={
                                                    handleUniversityTextChange
                                                }
                                                disabled={
                                                    sendingHomologation ||
                                                    hasSentHomologation
                                                }
                                            />
                                            <div className="mt-4 mb-6 space-y-4">
                                                {/* Tip: Separación de documentos */}
                                                <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-5 shadow-sm">
                                                    <div className="flex items-start gap-4">
                                                        <div className="p-2 bg-blue-100 text-blue-600 rounded-full shrink-0 mt-1">
                                                            <Icons.InfoCircle className="h-5 w-5" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h5 className="text-base font-bold text-blue-900 mb-4">
                                                                Guía paso a paso
                                                                para cargar tus
                                                                documentos de
                                                                homologación
                                                            </h5>

                                                            <div className="space-y-4">
                                                                {/* Paso 1 */}
                                                                <div className="flex gap-3">
                                                                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-800 text-xs font-bold">
                                                                        1
                                                                    </span>
                                                                    <p className="text-sm text-blue-900 pt-0.5">
                                                                        <strong>
                                                                            Selecciona
                                                                            la
                                                                            universidad
                                                                            de
                                                                            origen
                                                                        </strong>{' '}
                                                                        y digita
                                                                        el
                                                                        nombre
                                                                        del{' '}
                                                                        <strong>
                                                                            programa
                                                                        </strong>{' '}
                                                                        que
                                                                        cursaste.
                                                                    </p>
                                                                </div>

                                                                {/* Paso 2 */}
                                                                <div className="flex gap-3">
                                                                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-800 text-xs font-bold">
                                                                        2
                                                                    </span>
                                                                    <div className="text-sm text-blue-900 flex-1">
                                                                        <p className="mb-2 pt-0.5">
                                                                            <strong>
                                                                                Sube
                                                                                los
                                                                                documentos
                                                                                requeridos
                                                                                tal
                                                                                cual
                                                                                se
                                                                                mencionan:
                                                                            </strong>
                                                                        </p>
                                                                        <ul className="list-disc pl-5 space-y-1 text-blue-800 mb-3">
                                                                            <li>
                                                                                Un
                                                                                archivo
                                                                                para
                                                                                el{' '}
                                                                                <strong>
                                                                                    certificado
                                                                                    de
                                                                                    notas
                                                                                </strong>
                                                                                .
                                                                            </li>
                                                                            <li>
                                                                                Un
                                                                                archivo
                                                                                diferente
                                                                                para
                                                                                el{' '}
                                                                                <strong>
                                                                                    contenido
                                                                                    programático
                                                                                </strong>
                                                                                .
                                                                            </li>
                                                                            <li className="text-blue-700 italic bg-blue-100/50 p-1.5 rounded-md inline-block mt-1">
                                                                                <span className="font-semibold">
                                                                                    Nota
                                                                                    para
                                                                                    SENA:
                                                                                </span>{' '}
                                                                                Únicamente
                                                                                se
                                                                                requiere
                                                                                el
                                                                                certificado
                                                                                de
                                                                                notas.
                                                                            </li>
                                                                        </ul>

                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                                                            <div className="bg-white p-3 rounded-md border border-emerald-100 shadow-sm">
                                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                                    <Icons.CheckCircle className="w-4 h-4 text-emerald-600" />
                                                                                    <span className="font-semibold text-emerald-800 text-xs uppercase tracking-wide">
                                                                                        Lo
                                                                                        correcto
                                                                                    </span>
                                                                                </div>
                                                                                <p className="text-gray-600 text-xs leading-relaxed">
                                                                                    Subir
                                                                                    archivos
                                                                                    separados
                                                                                    para
                                                                                    notas
                                                                                    y
                                                                                    contenido
                                                                                    programático.
                                                                                </p>
                                                                            </div>
                                                                            <div className="bg-white p-3 rounded-md border border-rose-100 shadow-sm">
                                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                                    <Icons.Times className="w-4 h-4 text-rose-600" />
                                                                                    <span className="font-semibold text-rose-800 text-xs uppercase tracking-wide">
                                                                                        Evita
                                                                                        esto
                                                                                    </span>
                                                                                </div>
                                                                                <p className="text-gray-600 text-xs leading-relaxed">
                                                                                    No
                                                                                    unas
                                                                                    ambos
                                                                                    documentos
                                                                                    en
                                                                                    un
                                                                                    solo
                                                                                    PDF.
                                                                                    El
                                                                                    sistema
                                                                                    necesita
                                                                                    validarlos
                                                                                    por
                                                                                    separado.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Paso 3 */}
                                                                <div className="flex gap-3">
                                                                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-800 text-xs font-bold">
                                                                        3
                                                                    </span>
                                                                    <p className="text-sm text-blue-900 pt-0.5">
                                                                        Si
                                                                        tienes
                                                                        estudios
                                                                        en otra
                                                                        institución,
                                                                        usa la
                                                                        opción{' '}
                                                                        <strong>
                                                                            "Agregar
                                                                            Universidad"
                                                                        </strong>{' '}
                                                                        para
                                                                        incluirla.
                                                                    </p>
                                                                </div>

                                                                {/* Paso 4 */}
                                                                <div className="flex gap-3">
                                                                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-800 text-xs font-bold">
                                                                        4
                                                                    </span>
                                                                    <p className="text-sm text-blue-900 pt-0.5">
                                                                        Para
                                                                        finalizar,
                                                                        haz clic
                                                                        en el
                                                                        botón{' '}
                                                                        <strong>
                                                                            "Enviar
                                                                            Solicitud"
                                                                        </strong>
                                                                        . Esto
                                                                        enviará
                                                                        tus
                                                                        documentos
                                                                        para
                                                                        procesamiento
                                                                        e inicio
                                                                        de
                                                                        validación
                                                                        de tu
                                                                        homologación.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Tip: Soporte y Fallas - Solo mostrar si hay documentos rechazados */}
                                                {otherDocumentsOrdered.some(
                                                    (doc) => {
                                                        const name = (
                                                            doc.nameDocumentType ||
                                                            ''
                                                        )
                                                            .toLowerCase()
                                                            .normalize('NFD')
                                                            .replace(
                                                                /[\u0300-\u036f]/g,
                                                                ''
                                                            )
                                                        const isHomologation =
                                                            (name.includes(
                                                                'certificado'
                                                            ) &&
                                                                name.includes(
                                                                    'notas'
                                                                )) ||
                                                            (name.includes(
                                                                'contenidos'
                                                            ) &&
                                                                name.includes(
                                                                    'program'
                                                                ))
                                                        return (
                                                            isHomologation &&
                                                            doc.idDocumentStatus ===
                                                                DocumentStatus.Rechazado
                                                        )
                                                    }
                                                ) && (
                                                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-md shadow-sm">
                                                        <div className="flex">
                                                            <div className="flex-shrink-0">
                                                                <Icons.ExclamationTriangle
                                                                    className="h-6 w-6 text-orange-500"
                                                                    aria-hidden="true"
                                                                />
                                                            </div>
                                                            <div className="ml-3">
                                                                <h5 className="text-sm font-bold text-orange-800 mb-1">
                                                                    ¿Tienes
                                                                    problemas
                                                                    con algún
                                                                    documento?
                                                                </h5>
                                                                <p className="text-sm text-orange-700">
                                                                    Si un
                                                                    documento
                                                                    falla o es
                                                                    rechazado,
                                                                    revisa que
                                                                    cumpla con
                                                                    los
                                                                    requisitos y
                                                                    vuelve a
                                                                    intentarlo.
                                                                    <br />
                                                                    Si el
                                                                    problema
                                                                    persiste,
                                                                    contacta a
                                                                    nuestro
                                                                    equipo de
                                                                    soporte
                                                                    aquí:{' '}
                                                                    <a
                                                                        href="https://universidadean.edu.co/soporte"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="font-bold underline text-orange-900 hover:text-orange-600 transition-colors"
                                                                    >
                                                                        Soporte
                                                                        Ean
                                                                    </a>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {(() => {
                                            const isContProgram = (
                                                name?: string
                                            ) => {
                                                if (!name) return false
                                                const n = name
                                                    .toLowerCase()
                                                    .normalize('NFD')
                                                    .replace(
                                                        /[\u0300-\u036f]/g,
                                                        ''
                                                    )
                                                return (
                                                    n.includes('contenidos') &&
                                                    n.includes('program')
                                                )
                                            }
                                            const isSenaMain = (
                                                selectedUniversityText || ''
                                            )
                                                .toUpperCase()
                                                .includes('-SENA-')
                                            const normalize = (name?: string) =>
                                                (name || '')
                                                    .toLowerCase()
                                                    .normalize('NFD')
                                                    .replace(
                                                        /[\u0300-\u036f]/g,
                                                        ''
                                                    )
                                            const isCertNotas = (
                                                name?: string
                                            ) => {
                                                const n = normalize(name)
                                                return (
                                                    n.includes('certificado') &&
                                                    n.includes('notas')
                                                )
                                            }
                                            const docs =
                                                otherDocGroups[0] ||
                                                otherDocumentsOrdered

                                            // Para homologación, solo mostrar documentos de homologación
                                            const filtered = isSenaMain
                                                ? docs.filter((d) =>
                                                      isCertNotas(
                                                          d.nameDocumentType
                                                      )
                                                  )
                                                : docs.filter(
                                                      (d) =>
                                                          isCertNotas(
                                                              d.nameDocumentType
                                                          ) ||
                                                          isContProgram(
                                                              d.nameDocumentType
                                                          )
                                                  )
                                            return filtered
                                        })().map((document, index) => {
                                            const normalize = (name?: string) =>
                                                (name || '')
                                                    .toLowerCase()
                                                    .normalize('NFD')
                                                    .replace(
                                                        /[\u0300-\u036f]/g,
                                                        ''
                                                    )

                                            const isCertNotas = (
                                                name?: string
                                            ) => {
                                                const n = normalize(name)
                                                return (
                                                    n.includes('certificado') &&
                                                    n.includes('notas')
                                                )
                                            }

                                            const isContProgram = (
                                                name?: string
                                            ) => {
                                                const n = normalize(name)
                                                return (
                                                    n.includes('contenidos') &&
                                                    n.includes('program')
                                                )
                                            }

                                            const tourId = isCertNotas(
                                                document.nameDocumentType
                                            )
                                                ? 'tour-upload-notes'
                                                : isContProgram(
                                                        document.nameDocumentType
                                                    )
                                                  ? 'tour-upload-content'
                                                  : undefined

                                            console.log(
                                                `📄 OTHER DOCUMENT ${index}:`,
                                                {
                                                    idDocumentType:
                                                        document.idDocumentType,
                                                    nameDocumentType:
                                                        document.nameDocumentType,
                                                    idSapDocumentSubtest:
                                                        document.idSapDocumentSubtest,
                                                    idAdditionalDocument:
                                                        document.idAdditionalDocument,
                                                    fieldName:
                                                        getFieldName(document),
                                                    key: getFieldName(document),
                                                    tourId
                                                }
                                            )

                                            return (
                                                <div
                                                    key={getFieldName(
                                                        document,
                                                        index
                                                    )}
                                                    id={tourId}
                                                    className="w-full"
                                                >
                                                    <DocumentCard
                                                        document={document}
                                                        control={control}
                                                        watch={watch}
                                                        uploadIndividually={
                                                            false
                                                        }
                                                        uploadInProgress={
                                                            uploadInProgress
                                                        }
                                                        onDeleteDocument={
                                                            handleRequestDeleteDocument
                                                        }
                                                        onUploadSingleDocument={(
                                                            additionalDocumentId,
                                                            sapDocumentSubtestId,
                                                            file
                                                        ) =>
                                                            uploadSingleDocument(
                                                                additionalDocumentId,
                                                                sapDocumentSubtestId,
                                                                file,
                                                                false, // markAsPending
                                                                Number(
                                                                    getOriginKey(
                                                                        document
                                                                    )
                                                                )
                                                            )
                                                        }
                                                        onUploadWithHelp={(
                                                            additionalDocumentId,
                                                            sapDocumentSubtestId,
                                                            file
                                                        ) =>
                                                            uploadSingleDocument(
                                                                additionalDocumentId,
                                                                sapDocumentSubtestId,
                                                                file,
                                                                true, // markAsPending
                                                                Number(
                                                                    getOriginKey(
                                                                        document
                                                                    )
                                                                ) // originIndex
                                                            )
                                                        }
                                                        text={text}
                                                        isPaymentCompleted={
                                                            isPaymentCompleted
                                                        }
                                                    />
                                                </div>
                                            )
                                        })}
                                    </div>

                                    {/* Secciones adicionales de universidad de origen */}
                                    {additionalOrigins.map((section, idx) => {
                                        const hasData = Boolean(
                                            (section.selectedUniversityText &&
                                                section.selectedUniversityText.trim()) ||
                                                (section.selectedProgram &&
                                                    section.selectedProgram.trim()) ||
                                                (section.selectedUniversity &&
                                                    section.selectedUniversity.trim())
                                        )
                                        const hasDocs =
                                            (otherDocGroups[idx + 1] || [])
                                                .length > 0
                                        if (!hasData && !hasDocs) return null
                                        return (
                                            <div
                                                key={section.id}
                                                className="mt-8 mb-8 pb-4 border-b"
                                            >
                                                <div className="flex items-center justify-between mb-4 pb-2 border-b">
                                                    <div className="flex items-center">
                                                        <Icons.File className="w-5 h-5 text-gray-500 mr-2" />
                                                        <SubTitle
                                                            subTitle={`Universidad de origen adicional #${idx + 1}`}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={`text-red-600 text-sm hover:underline ${
                                                            deletingOrigin[
                                                                section.id
                                                            ]
                                                                ? 'opacity-60 cursor-not-allowed'
                                                                : ''
                                                        }`}
                                                        onClick={() =>
                                                            handleRemoveOriginSection(
                                                                section.id,
                                                                idx
                                                            )
                                                        }
                                                        disabled={
                                                            deletingOrigin[
                                                                section.id
                                                            ] ||
                                                            sendingHomologation ||
                                                            hasSentHomologation
                                                        }
                                                    >
                                                        {deletingOrigin[
                                                            section.id
                                                        ] ? (
                                                            <span className="inline-flex items-center">
                                                                <Icons.Spinner className="w-4 h-4 animate-spin mr-1" />{' '}
                                                                Eliminando...
                                                            </span>
                                                        ) : (
                                                            'Eliminar'
                                                        )}
                                                    </button>
                                                </div>
                                                <HomologationSelectors
                                                    selectedUniversity={
                                                        section.selectedUniversity
                                                    }
                                                    selectedProgram={
                                                        section.selectedProgram
                                                    }
                                                    selectedUniversityText={
                                                        section.selectedUniversityText
                                                    }
                                                    onUniversityChange={(
                                                        value
                                                    ) =>
                                                        updateOriginSection(
                                                            section.id,
                                                            {
                                                                selectedUniversity:
                                                                    value
                                                            }
                                                        )
                                                    }
                                                    onProgramChange={(value) =>
                                                        updateOriginSection(
                                                            section.id,
                                                            {
                                                                selectedProgram:
                                                                    value
                                                            }
                                                        )
                                                    }
                                                    onUniversityTextChange={(
                                                        text
                                                    ) =>
                                                        updateOriginSection(
                                                            section.id,
                                                            {
                                                                selectedUniversityText:
                                                                    text
                                                            }
                                                        )
                                                    }
                                                    disabled={
                                                        sendingHomologation ||
                                                        hasSentHomologation
                                                    }
                                                />

                                                {/* Cajas de archivos para esta sección */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {(() => {
                                                        const isContProgram = (
                                                            name?: string
                                                        ) => {
                                                            if (!name)
                                                                return false
                                                            const n = name
                                                                .toLowerCase()
                                                                .normalize(
                                                                    'NFD'
                                                                )
                                                                .replace(
                                                                    /[\u0300-\u036f]/g,
                                                                    ''
                                                                )
                                                            return (
                                                                n.includes(
                                                                    'contenidos'
                                                                ) &&
                                                                n.includes(
                                                                    'program'
                                                                )
                                                            )
                                                        }
                                                        const isSenaSection = (
                                                            section.selectedUniversityText ||
                                                            ''
                                                        )
                                                            .toUpperCase()
                                                            .includes('-SENA-')
                                                        const normalize = (
                                                            name?: string
                                                        ) =>
                                                            (name || '')
                                                                .toLowerCase()
                                                                .normalize(
                                                                    'NFD'
                                                                )
                                                                .replace(
                                                                    /[\u0300-\u036f]/g,
                                                                    ''
                                                                )
                                                        const isCertNotas = (
                                                            name?: string
                                                        ) => {
                                                            const n =
                                                                normalize(name)
                                                            return (
                                                                n.includes(
                                                                    'certificado'
                                                                ) &&
                                                                n.includes(
                                                                    'notas'
                                                                )
                                                            )
                                                        }
                                                        const docs =
                                                            otherDocGroups[
                                                                idx + 1
                                                            ] || []
                                                        const filtered =
                                                            isSenaSection
                                                                ? docs.filter(
                                                                      (d) =>
                                                                          isCertNotas(
                                                                              d.nameDocumentType
                                                                          )
                                                                  )
                                                                : docs.filter(
                                                                      (d) =>
                                                                          isCertNotas(
                                                                              d.nameDocumentType
                                                                          ) ||
                                                                          isContProgram(
                                                                              d.nameDocumentType
                                                                          )
                                                                  )
                                                        return filtered
                                                    })().map(
                                                        (document, index) => (
                                                            <DocumentCard
                                                                key={`${section.id}_${getFieldName(document, index)}`}
                                                                document={
                                                                    document
                                                                }
                                                                control={
                                                                    control
                                                                }
                                                                watch={watch}
                                                                uploadIndividually={
                                                                    false
                                                                }
                                                                uploadInProgress={
                                                                    uploadInProgress
                                                                }
                                                                onDeleteDocument={
                                                                    handleRequestDeleteDocument
                                                                }
                                                                onUploadSingleDocument={(
                                                                    additionalDocumentId,
                                                                    sapDocumentSubtestId,
                                                                    file,
                                                                    fieldKeyOverride
                                                                ) =>
                                                                    uploadSingleDocument(
                                                                        additionalDocumentId,
                                                                        sapDocumentSubtestId,
                                                                        file,
                                                                        false, // markAsPending
                                                                        Number(
                                                                            getOriginKey(
                                                                                document
                                                                            )
                                                                        ),
                                                                        fieldKeyOverride
                                                                    )
                                                                }
                                                                text={text}
                                                                isPaymentCompleted={
                                                                    isPaymentCompleted
                                                                }
                                                                fieldNameOverride={`${section.id}_${getFieldName(document)}`}
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {admissionTypeAcademic === '001' && (
                                        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                                            {/* Card 1: Agregar Universidad */}
                                            <div
                                                id="tour-add-university"
                                                className={`relative flex flex-col p-5 rounded-xl border-2 transition-all duration-200 ${
                                                    !hasSentHomologation &&
                                                    canAddOrigin
                                                        ? 'border-emerald-100 bg-emerald-50/30 hover:border-emerald-200 hover:shadow-md'
                                                        : 'border-gray-100 bg-gray-50 opacity-70 grayscale'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div
                                                        className={`p-2.5 rounded-lg ${
                                                            !hasSentHomologation &&
                                                            canAddOrigin
                                                                ? 'bg-emerald-100 text-emerald-600'
                                                                : 'bg-gray-200 text-gray-500'
                                                        }`}
                                                    >
                                                        <Icons.University className="w-5 h-5" />
                                                    </div>
                                                    <h4
                                                        className={`font-bold ${
                                                            !hasSentHomologation &&
                                                            canAddOrigin
                                                                ? 'text-emerald-900'
                                                                : 'text-gray-500'
                                                        }`}
                                                    >
                                                        Agregar Universidad
                                                    </h4>
                                                </div>
                                                <p className="text-xs text-gray-600 mb-6 flex-grow leading-relaxed">
                                                    ¿Estudiaste en más de una
                                                    institución? Usa esta opción
                                                    para incluir otra
                                                    universidad y sus
                                                    documentos.
                                                </p>
                                                <Tooltip content="Agrega otra universidad si tienes estudios en múltiples instituciones">
                                                    <button
                                                        type="button"
                                                        className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold shadow-sm transition-all flex items-center justify-center gap-2
                                                    ${
                                                        !hasSentHomologation &&
                                                        canAddOrigin &&
                                                        !addingOrigin &&
                                                        !sendingHomologation
                                                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
                                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                                        onClick={
                                                            handleAddOriginSection
                                                        }
                                                        disabled={
                                                            hasSentHomologation ||
                                                            !canAddOrigin ||
                                                            addingOrigin ||
                                                            sendingHomologation
                                                        }
                                                    >
                                                        {addingOrigin ? (
                                                            <Icons.Spinner className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Icons.Minus className="w-4 h-4 rotate-90" />
                                                        )}
                                                        {addingOrigin
                                                            ? 'Agregando...'
                                                            : 'Agregar nueva'}
                                                    </button>
                                                </Tooltip>
                                            </div>

                                            {/* Card 2: Reactivar */}
                                            {hasSentHomologation &&
                                                hasRejectedHomologationDocs && (
                                                    <div
                                                        className={`relative flex flex-col p-5 rounded-xl border-2 transition-all duration-200 border-orange-100 bg-orange-50/30 hover:border-orange-200 hover:shadow-md`}
                                                    >
                                                        <div className="flex items-center gap-3 mb-3">
                                                            <div
                                                                className={`p-2.5 rounded-lg bg-orange-100 text-orange-600`}
                                                            >
                                                                <Icons.Process className="w-5 h-5" />
                                                            </div>
                                                            <h4
                                                                className={`font-bold text-orange-900`}
                                                            >
                                                                Reactivar
                                                                Edición
                                                            </h4>
                                                        </div>
                                                        <p className="text-xs text-gray-600 mb-6 flex-grow leading-relaxed">
                                                            Habilita la edición
                                                            solo si necesitas
                                                            corregir documentos
                                                            que fueron
                                                            rechazados en la
                                                            revisión.
                                                        </p>
                                                        <Tooltip content="Habilita la edición si necesitas corregir documentos rechazados">
                                                            <button
                                                                type="button"
                                                                className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold shadow-sm transition-all flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white shadow-orange-200`}
                                                                onClick={() => {
                                                                    setHasSentHomologation(
                                                                        false
                                                                    )
                                                                    if (
                                                                        idAspirant
                                                                    ) {
                                                                        localStorage.setItem(
                                                                            `hasSentHomologation_${idAspirant}`,
                                                                            'false'
                                                                        )
                                                                    } else {
                                                                        localStorage.setItem(
                                                                            'hasSentHomologation_global',
                                                                            'false'
                                                                        )
                                                                    }
                                                                }}
                                                            >
                                                                <Icons.Process className="w-4 h-4" />
                                                                Reactivar
                                                            </button>
                                                        </Tooltip>
                                                    </div>
                                                )}

                                            {/* Card 3: Enviar */}
                                            <div
                                                id="tour-send-button"
                                                className={`relative flex flex-col p-5 rounded-xl border-2 transition-all duration-200 ${
                                                    canSendHomologation &&
                                                    !hasSentHomologation
                                                        ? 'border-blue-100 bg-blue-50/30 hover:border-blue-200 hover:shadow-md'
                                                        : 'border-gray-100 bg-gray-50 opacity-70 grayscale'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div
                                                        className={`p-2.5 rounded-lg ${
                                                            canSendHomologation &&
                                                            !hasSentHomologation
                                                                ? 'bg-blue-100 text-blue-600'
                                                                : 'bg-gray-200 text-gray-500'
                                                        }`}
                                                    >
                                                        <Icons.Upload className="w-5 h-5" />
                                                    </div>
                                                    <h4
                                                        className={`font-bold ${
                                                            canSendHomologation &&
                                                            !hasSentHomologation
                                                                ? 'text-blue-900'
                                                                : 'text-gray-500'
                                                        }`}
                                                    >
                                                        Enviar Solicitud
                                                    </h4>
                                                </div>
                                                <p className="text-xs text-gray-600 mb-6 flex-grow leading-relaxed">
                                                    Finaliza el proceso para que
                                                    nuestro equipo inicie el
                                                    estudio de tus documentos.
                                                </p>
                                                <Tooltip content="Envía tu solicitud para estudio de homologación">
                                                    <button
                                                        type="button"
                                                        className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold shadow-sm transition-all flex items-center justify-center gap-2
                                                    ${
                                                        canSendHomologation &&
                                                        !sendingHomologation &&
                                                        !hasSentHomologation
                                                            ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200'
                                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                                        onClick={
                                                            handleManualHomologation
                                                        }
                                                        disabled={
                                                            !canSendHomologation ||
                                                            sendingHomologation ||
                                                            hasSentHomologation
                                                        }
                                                    >
                                                        {sendingHomologation ? (
                                                            <Icons.Spinner className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Icons.Upload className="w-4 h-4" />
                                                        )}
                                                        {sendingHomologation
                                                            ? 'Enviando...'
                                                            : 'Enviar todo'}
                                                    </button>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </section>

                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                    hideSubmit={!hasNextStep}
                    hidePrevious={false}
                    submitLabel={
                        hasNextStep
                            ? t('forms.ui.next', 'Siguiente')
                            : t('forms.ui.save', 'Guardar')
                    }
                />
                {/* Modal de confirmación de eliminación */}
                <ConfirmDialog
                    isOpen={confirmDeleteOpen}
                    onOpenChange={setConfirmDeleteOpen}
                    title={t(
                        'uploadFiles.confirmDeleteTitle',
                        'Confirmar eliminación'
                    )}
                    message={t(
                        'uploadFiles.confirmDelete',
                        '¿Estás seguro de querer eliminar este documento?'
                    )}
                    cancelText={t('cancelar', 'Cancelar')}
                    confirmText={t('eliminar', 'Eliminar')}
                    confirmColor={'danger'}
                    icon={
                        <Icons.ExclamationTriangle className="w-5 h-5 text-danger" />
                    }
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDeleteId(undefined)}
                    backdrop="opaque"
                    placement="center"
                />
                <ConfirmDialog
                    isOpen={confirmSendOpen}
                    onOpenChange={setConfirmSendOpen}
                    title={t(
                        'uploadFiles.confirmSendTitle',
                        'Confirmar envío de homologación'
                    )}
                    message={t(
                        'uploadFiles.confirmSendMessage',
                        '¿Deseas agregar otra universidad de origen antes de enviar la homologación? Puedes agregar una más o continuar con el envío.'
                    )}
                    cancelText={t(
                        'uploadFiles.addOrigin',
                        'Agregar universidad de origen'
                    )}
                    confirmText={t(
                        'uploadFiles.sendHomologation',
                        'Enviar solicitud'
                    )}
                    confirmColor={'primary'}
                    icon={<Icons.InfoCircle className="w-5 h-5 text-primary" />}
                    onConfirm={startHomologationSend}
                    onCancel={handleAddOriginSection}
                    backdrop="opaque"
                    placement="center"
                />
                {showDiscountModal === true && (
                    <AlertModal
                        isOpen={true}
                        onOpenChange={(val) => {
                            if (!val) {
                                setShowDiscountModal(false)
                            }
                        }}
                        title={t('uploadFiles.discountModalTitle')}
                        message={t('uploadFiles.discountModalMessage')}
                        buttonText={t('messageCard.buttons.understood')}
                        buttonColor="primary"
                        icon={
                            <Icons.InfoCircle className="w-5 h-5 text-primary" />
                        }
                        backdrop="opaque"
                        placement="center"
                    />
                )}
                {paymentHomologationOrderModalOpen && (
                    <AlertModal
                        isOpen={true}
                        onOpenChange={(val) => {
                            if (!val) {
                                setPaymentHomologationOrderModalOpen(false)
                            }
                        }}
                        title={t(
                            'uploadFiles.paymentHomologationOrderModalTitle'
                        )}
                        message={t(
                            'uploadFiles.paymentHomologationOrderModalMessage'
                        )}
                        buttonText={t('messageCard.buttons.understood')}
                        buttonColor="primary"
                        icon={
                            <Icons.InfoCircle className="w-5 h-5 text-primary" />
                        }
                        backdrop="opaque"
                        placement="center"
                    />
                )}
                <HomologationTour
                    isOpen={isTourOpen}
                    onClose={handleTourClose}
                    steps={tourSteps}
                    onComplete={handleTourComplete}
                />
            </form>
        </div>
    )
}
